<header class="url-detail-hero" *ngIf="!isEmbedMode()">
  <h2 class="url-detail-title">URL Detail</h2>
  <a
    class="url-detail-page-link"
    [href]="url"
    target="_blank"
    rel="noopener noreferrer"
    *ngIf="url"
  >
    <span class="url-detail-page-link-text">{{ url }}</span>
    <span class="url-detail-page-link-icon" aria-hidden="true">↗</span>
  </a>
  <div class="url-detail-dev-actions" *ngIf="isDevView() && url">
    <button
      type="button"
      class="btn-secondary btn-sm"
      (click)="refetchSinglePageAnalysis()"
      [disabled]="pageRefetchRunning"
      title="Dev-only: force a fresh analysis task for this URL only."
    >{{ pageRefetchRunning ? 'Re-fetching page…' : 'Re-fetch This Page' }}</button>
    <button
      type="button"
      class="btn-secondary btn-sm"
      (click)="devRebuildSupportingPagesByTopic()"
      [disabled]="supportingPagesV1RebuildRunning"
      title="Dev-only: rebuild Supporting pages by topic snapshot for this URL (replaces stored data)."
    >{{ supportingPagesV1RebuildRunning ? 'Building supporting pages…' : 'Build Supporting Pages' }}</button>
    <button
      type="button"
      class="btn-secondary btn-sm"
      (click)="devRebuildSupportingPagesOpportunities()"
      [disabled]="supportingPagesOppRebuildRunning"
      title="Dev-only: rebuild Supporting page opportunities snapshot for this URL (replaces stored data)."
    >{{ supportingPagesOppRebuildRunning ? 'Building opportunities…' : 'Build Opportunities' }}</button>
    <span class="muted" *ngIf="pageRefetchStatus">{{ pageRefetchStatus }}</span>
    <span class="muted" *ngIf="supportingPagesRebuildStatus">{{ supportingPagesRebuildStatus }}</span>
  </div>
</header>

<section class="url-focus" aria-label="Focus">
  <p class="url-focus-status muted" *ngIf="analysisStatus === 'loading'">Loading health and SERP context…</p>
  <p class="url-focus-status muted" *ngIf="analysisStatus === 'error' || (analysisStatus === 'success' && !analysisData)">
    Run a crawl and analysis for this URL to see health and SERP context.
  </p>

  <!-- Dashboard package row, expanded: evidence rows, not verdict chips.
       Every row names the number behind its verdict, and the ones worth acting
       on carry the same Dismiss / Work item pair the Issues drawer has — which
       is the whole point: triage without leaving the Dashboard. -->
  <ng-container *ngIf="isCompactPeek() && analysisStatus === 'success' && analysisData">
    <p class="url-focus-status muted" *ngIf="!compactPeekReady()">Loading Issues signals…</p>

    <ng-container *ngIf="compactPeekReady()">
    <div class="url-ev" *ngIf="compactPeekPanel() as panel">
      <!-- The comparison is an action here, not a diagnosis. Its numbers —
           word count, ranked keywords — are things to read rather than things
           to do, so they live in the drawer this opens; what survives in the
           recap is the one click and the rival's name. -->
      <div class="url-ev__lead" *ngIf="compareRowTargetDomain() as rival">
        <button type="button" class="btn btn--sm" (click)="openCompareFromPeek()">
          Compare vs {{ rival }}
        </button>
      </div>

      <div class="url-ev__row" *ngFor="let row of panel.attention">
        <span class="url-ev__name">{{ row.title }}</span>
        <div class="url-ev__main">
          <span class="badge badge--caps" [ngClass]="diagnosisChipClass(row.tone)">{{ row.label }}</span>
          <p class="url-ev__fact">{{ row.fact }}</p>
          <ul class="url-ev__spec" *ngIf="row.specifics.length">
            <li *ngFor="let s of row.specifics">{{ s }}</li>
          </ul>
        </div>
        <!-- The click saving. Same component, same payloads, same queue as the
             Issues drawer; it just no longer costs a navigation to reach.
             `as payload` narrows the nullable off `row` — the input is
             required and non-null, and a row with nothing to triage has none. -->
        <ng-container *ngIf="row.payload as payload">
          <app-plan-v2-issue-card-actions
            [reviewSelect]="embedReviewSurface"
            [reviewPending]="reviewSupportingPagesPending()"
            class="url-ev__act"
            *ngIf="showPeekActions(payload)"
            [payload]="payload"
            [domain]="planV2IssueDomain()"
            [compact]="true"
          />
        </ng-container>
      </div>

      <p class="url-ev__more" *ngIf="panel.attentionHidden > 0">
        +{{ panel.attentionHidden }} more
        {{ panel.attentionHidden === 1 ? 'diagnosis' : 'diagnoses' }} need attention
      </p>

      <!-- Measured, nothing to do. One line, with the number that proves it ran. -->
      <p class="url-ev__clear" *ngIf="panel.clear.length">
        <span class="url-ev__clear-k">Clear</span>
        <span class="url-ev__clear-i" *ngFor="let row of panel.clear">
          <b>{{ row.title }}</b> — {{ row.fact }}
        </span>
      </p>

      <!-- NOT "clear". A check that did not run shares a colour with one that
           passed, and they are not the same answer. -->
      <p class="url-ev__unchecked" *ngIf="panel.unchecked.length">
        <span class="url-ev__clear-k">Not checked</span>
        <span class="url-ev__clear-i" *ngFor="let row of panel.unchecked">
          <b>{{ row.title }}</b> — {{ row.fact }}
        </span>
      </p>

      <p class="url-peek-empty muted" *ngIf="compactPeekPanelEmpty()">
        No open Issues signals for this page.
      </p>
    </div>
    </ng-container>
  </ng-container>

  <ng-container *ngIf="!isCompactPeek() && analysisStatus === 'success' && analysisData">
    <section
      class="url-focus-row url-focus-row--next-action"
      *ngIf="!isPlanV2Embed() && isActionPlanV2Enabled()"
      aria-labelledby="url-next-action-heading"
    >
      <h3 id="url-next-action-heading" class="url-focus-row-heading">Next action</h3>
      <p class="url-next-action-period muted" *ngIf="planV2Period as period" [title]="planV2PeriodHint()">
        {{ period.label }}
      </p>
      <p class="muted" *ngIf="planV2NextActionLoading">Loading synthesis…</p>
      <p class="url-focus-error muted" *ngIf="planV2NextActionError">{{ planV2NextActionError }}</p>
      <article class="card card--pad url-focus-card url-focus-card--next-action" *ngIf="planV2NextAction && !planV2NextActionLoading">
        <p class="url-next-action-primary">
          <strong>{{ planV2NextAction.recommendedAction.label }}</strong>
          <span class="score-badge url-next-action-score">{{ planV2NextAction.priorityScore | number:'1.0-0' }}</span>
        </p>
        <ul class="url-next-action-explain muted">
          <li *ngFor="let line of planV2NextAction.explain.slice(0, 5)">{{ line }}</li>
        </ul>
        <button type="button" class="btn-secondary btn-sm" (click)="openPlanV2InInventory()">View in Plan v2</button>
      </article>
      <p class="muted" *ngIf="!planV2NextActionLoading && !planV2NextActionError && !planV2NextAction">
        No open recommended action in Plan v2 for this URL.
      </p>
    </section>

    <section
      class="url-focus-row url-focus-row--plan-v2-playbook"
      *ngIf="showPlanV2ExpandPlaybook() && planV2ExpandPlaybook() as playbook"
      aria-labelledby="url-plan-v2-playbook-heading"
    >
      <div
        class="url-claim-override-progress url-serp-inline-progress"
        *ngIf="claimOverrideProgress"
        role="status"
        aria-live="polite"
      >
        <p class="url-claim-override-progress-heading">Refreshing after intent change</p>
        <div
          class="url-serp-inline-progress-bar"
          [class.url-serp-inline-progress-bar--indeterminate]="claimOverrideProgress.indeterminate"
        >
          <div
            class="url-serp-inline-progress-fill"
            [style.width.%]="claimOverrideProgress.indeterminate ? null : claimOverrideProgress.percent"
          ></div>
        </div>
        <p class="url-serp-inline-progress-line">{{ claimOverrideProgress.statusLine }}</p>
        <p class="url-serp-inline-progress-eta muted" *ngIf="claimOverrideProgress.etaLabel">
          {{ claimOverrideProgress.etaLabel }}
        </p>
      </div>
      <h3 id="url-plan-v2-playbook-heading" class="url-focus-row-heading">Prioritize playbook</h3>
      <article class="card card--pad url-focus-card" [class.url-focus-card--refreshing]="claimOverrideSaving">
        <dl class="url-focus-row-cards url-focus-row-cards--overview">
          <div class="card card--pad url-focus-card url-focus-card-stat">
            <h4 class="url-focus-card-title">Claim</h4>
            <p class="url-focus-stat-value">{{ playbook.header.claimLabel }}</p>
          </div>
          <div class="card card--pad url-focus-card url-focus-card-stat">
            <h4 class="url-focus-card-title">Gap</h4>
            <p class="url-focus-stat-value">{{ playbook.header.gapLabel }}</p>
          </div>
          <div class="card card--pad url-focus-card url-focus-card-stat">
            <h4 class="url-focus-card-title">Ratio</h4>
            <p class="url-focus-stat-value">{{ playbook.header.ratioLabel }}</p>
          </div>
          <div class="card card--pad url-focus-card url-focus-card-stat">
            <h4 class="url-focus-card-title">KD</h4>
            <p class="url-focus-stat-value">{{ playbook.header.kdLabel }}</p>
          </div>
          <div class="card card--pad url-focus-card url-focus-card-stat" *ngIf="playbook.header.ceilingDriverKeyword as driver">
            <h4 class="url-focus-card-title">Ceiling driver</h4>
            <p class="url-focus-stat-value">{{ driver }}</p>
          </div>
        </dl>
        <p class="muted">{{ playbook.header.queueRationale }}</p>
        <ul class="url-next-action-explain muted">
          <li *ngFor="let step of playbook.steps">
            <button
              type="button"
              class="btn-link-inline"
              (click)="onPlanV2PlaybookStep(step)"
              [disabled]="claimOverrideSaving"
            >
              {{ step.label }}
            </button>
          </li>
        </ul>
      </article>
    </section>

    <section class="url-focus-row url-focus-row--embed-mini" *ngIf="showEmbedMiniOverviewSection()" aria-labelledby="url-embed-overview-heading">
      <h3 id="url-embed-overview-heading" class="url-focus-row-heading">Overview</h3>
      <div class="url-focus-row-cards url-focus-row-cards--overview">
        <article class="card card--pad url-focus-card url-focus-card-stat">
          <h4 class="url-focus-card-title">Accountable for / main keyword</h4>
          <p class="url-focus-stat-value" [ngClass]="pageDemandClaimClass()" [attr.title]="pageDemandClaimTitle()">{{ pageDemandClaimLabel() }}</p>
          <button
            type="button"
            class="btn-link-inline url-claim-override-trigger"
            *ngIf="pageDemand?.claim?.label"
            (click)="openClaimOverrideModal()"
            [disabled]="claimOverrideSaving"
          >Wrong intent?</button>
        </article>
        <article class="card card--pad url-focus-card url-focus-card-stat">
          <h4 class="url-focus-card-title">Keywords</h4>
          <p class="url-focus-stat-value" [attr.title]="rankedKeywordsCountTitle()">{{ rankedKeywordsCountLabel() }}</p>
        </article>
      </div>
    </section>

    <section class="url-focus-row" *ngIf="!isPlanV2Embed()" aria-labelledby="url-overview-heading">
      <h3 id="url-overview-heading" class="url-focus-row-heading">Overview</h3>
      <div class="url-focus-row-cards url-focus-row-cards--overview">
        <article class="card card--pad url-focus-card url-focus-card-status">
          <h4 class="url-focus-card-title">Health</h4>
          <p class="url-focus-status-value">
            <span class="badge" [ngClass]="effectiveHealthClass()">{{ effectiveHealthLabel() }}</span>
          </p>
        </article>
        <article class="card card--pad url-focus-card url-focus-card-stat">
          <h4 class="url-focus-card-title">Status code</h4>
          <p class="url-focus-stat-value">{{ analysisData.statusCode }}</p>
        </article>
        <article class="card card--pad url-focus-card url-focus-card-onpage">
          <h4 class="url-focus-card-title">Technical OnPage Score</h4>
          <p class="url-focus-onpage-score" [ngClass]="getScoreClass(analysisData.onpageScore)">
            {{ technicalOnPageScoreLabel() }}
          </p>
          <p class="url-focus-onpage-issues muted" *ngIf="technicalOnPageIssuesLabel() as issues">{{ issues }}</p>
        </article>
        <article class="card card--pad url-focus-card url-focus-card-stat">
          <h4 class="url-focus-card-title">Accountable for / main keyword</h4>
          <p class="url-focus-stat-value" [ngClass]="pageDemandClaimClass()" [attr.title]="pageDemandClaimTitle()">{{ pageDemandClaimLabel() }}</p>
          <button
            type="button"
            class="btn-link-inline url-claim-override-trigger"
            *ngIf="pageDemand?.claim?.label"
            (click)="openClaimOverrideModal()"
            [disabled]="claimOverrideSaving"
          >Wrong intent?</button>
        </article>
        <article class="card card--pad url-focus-card url-focus-card-stat">
          <h4 class="url-focus-card-title">Keywords</h4>
          <p class="url-focus-stat-value" [attr.title]="rankedKeywordsCountTitle()">{{ rankedKeywordsCountLabel() }}</p>
        </article>
        <article class="card card--pad url-focus-card url-focus-card-stat">
          <h4 class="url-focus-card-title">Est. traffic</h4>
          <p class="url-focus-stat-value" [attr.title]="estimatedTrafficTitle()">{{ estimatedTrafficLabel() }}</p>
        </article>
        <article class="card card--pad url-focus-card url-focus-card-stat">
          <h4 class="url-focus-card-title">Gap · visibility</h4>
          <p class="url-focus-stat-value" [attr.title]="pageRealizationStatTitle()">{{ pageRealizationStatLabel() }}</p>
        </article>
        <article class="card card--pad url-focus-card url-focus-card-log" *ngIf="isDevView()">
          <h4 class="url-focus-card-title">Log</h4>
          <p class="url-focus-card-lead">{{ improvementsSummary() }}</p>
          <ul class="url-focus-list" *ngIf="improvementsWhyBullets().length">
            <li *ngFor="let bullet of improvementsWhyBullets()">{{ bullet }}</li>
          </ul>
        </article>
      </div>
    </section>

    <!-- Run comparison (FW-CRAWL-DELTA-01 phase 3). Its own component so its
         styles do not land in url-detail.css, which is already over the
         per-component budget (TECHNICAL_DEBT §0t). -->
    <app-url-run-comparison *ngIf="!isPlanV2Embed() && pageUrlId" [urlId]="pageUrlId"></app-url-run-comparison>

    <section class="url-focus-row" *ngIf="showEmbedContentSection()" aria-labelledby="url-content-heading">
      <h3 id="url-content-heading" class="url-focus-row-heading">Content &amp; Topics</h3>
      <div class="url-focus-row-cards url-focus-row-cards--content">
        <article class="card card--pad url-focus-card url-focus-card-stat url-focus-card-wordcount" *ngIf="!isPlanV2Embed()">
          <h4 class="url-focus-card-title">Word count</h4>
          <p class="url-focus-stat-value">{{ analysisData.wordCount.toLocaleString() }}</p>
        </article>

        <article
          *ngIf="!isPlanV2Embed()"
          class="card card--pad url-focus-card url-focus-card-topics"
          [class.url-focus-card-topics--user]="!isDevView()"
        >
          <h4 class="url-focus-card-title">Top topics</h4>
          <p class="url-content-topics-empty muted" *ngIf="!hasTopicSignals()">
            No topic signals yet. They appear after keyword density and topic mapping have run for this page.
          </p>
          <div class="url-top-topics-chips" *ngIf="hasTopicSignals() && !isDevView()">
            <span
              class="badge badge--pill url-top-topic-chip"
              *ngFor="let signal of getTopPageTopicSignalsForDisplay(); trackBy: trackByTopicSignal"
              [class.url-top-topic-chip--claim-match]="topicSignalMatchesClaim(signal)"
            >
              <span class="url-top-topic-chip-label">{{ signal.label }}</span>
              <span class="url-top-topic-chip-badge" *ngIf="topicSignalMatchesClaim(signal)">Matches demand claim</span>
              <span
                class="url-top-topic-chip-pct"
                [ngClass]="getScoreClass(pageTopicRelevanceScore0to100(signal))"
              >{{ formatPageTopicRelevancePct(signal) }}</span>
            </span>
          </div>
          <ng-container *ngIf="hasTopicSignals() && isDevView()">
            <div class="page-topics-table-wrap" *ngIf="!pageTopicsExpanded">
              <table class="table table--dense page-topics-table">
                <thead>
                  <tr>
                    <th scope="col">Topic</th>
                    <th scope="col">Competitors</th>
                    <th scope="col" class="table__num">Relevance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let signal of getTopPageTopicSignals(); trackBy: trackByTopicSignal">
                    <td class="topic-label">{{ signal.label }}</td>
                    <td class="topic-competitors">
                      <ng-container *ngTemplateOutlet="topicCompetitorsCell; context: { signal: signal }"></ng-container>
                    </td>
                    <td class="table__num">
                      <span
                        class="badge"
                        [ngClass]="scoreBadgeClass(pageTopicRelevanceScore0to100(signal))"
                      >
                        {{ formatPageTopicRelevancePct(signal) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <div class="page-topics-table-wrap" *ngIf="pageTopicsExpanded">
              <table class="table table--dense page-topics-table">
                <thead>
                  <tr>
                    <th scope="col">Topic</th>
                    <th scope="col">Slug</th>
                    <th scope="col">Competitors</th>
                    <th scope="col" class="table__num">Weight</th>
                    <th scope="col" class="table__num">Salience</th>
                    <th scope="col" class="table__num">Rel. density</th>
                    <th scope="col" class="table__num">Relevance</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let signal of getPageTopicSignals(); trackBy: trackByTopicSignal">
                    <td class="topic-label">{{ signal.label }}</td>
                    <td class="topic-slug"><code>{{ signal.topicSlug }}</code></td>
                    <td class="topic-competitors">
                      <ng-container *ngTemplateOutlet="topicCompetitorsCell; context: { signal: signal }"></ng-container>
                    </td>
                    <td class="table__num">{{ formatTopicMetric(signal.weight) }}</td>
                    <td class="table__num">{{ formatTopicMetric(signal.salience) }}</td>
                    <td class="table__num">{{ formatTopicMetric(signal.relativeDensity) }}</td>
                    <td class="table__num">
                      <span
                        class="badge"
                        [ngClass]="scoreBadgeClass(pageTopicRelevanceScore0to100(signal))"
                      >
                        {{ formatPageTopicRelevancePct(signal) }}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>

            <button
              type="button"
              class="url-content-topics-expand"
              *ngIf="hasMorePageTopicsThanTop() || pageTopicsExpanded"
              (click)="togglePageTopicsExpanded()"
              [attr.aria-expanded]="pageTopicsExpanded"
            >{{ pageTopicsExpandControlLabel() }}</button>
          </ng-container>
        </article>

        <article
          *ngIf="showEmbedSupportingPagesCard() && buildTopicOwnershipIssuePayload() as topicPayload"
          class="card card--pad url-focus-card url-focus-card-topic-ownership url-focus-card-topic-ownership--unified"
          [class.url-focus-card--composing]="topicOwnershipComposing"
          [ngClass]="planV2CardExitClass(topicPayload.issueKey)"
        >
          <div class="url-focus-card-top">
            <!-- One name on every surface. The Dashboard peek that leads here
                 says "Supporting pages", and a card that renames itself between
                 the summary and the screen you reach from it makes the reader
                 check whether they are the same thing. -->
            <h4 class="url-focus-card-title">Supporting pages</h4>
            <!-- A verdict, so it waits for one. Rendering GAP FOUND over an
                 empty body is the card asserting something it cannot show. -->
            <span
              class="badge badge--caps"
              *ngIf="showTopicOwnershipGapChip() && !reviewSupportingPagesPending()"
              [ngClass]="diagnosisChipClass(topicOwnershipGapChipTone())"
            >{{ topicOwnershipGapChipLabel() }}</span>
            <span class="url-focus-checking muted" *ngIf="reviewSupportingPagesPending()"
              >Checking…</span
            >
          </div>

          <ng-template
            #headingPathTree
            let-segments
            let-composeSelect="composeSelect"
            let-v2Row="v2Row"
            let-sc="sc"
          >
            <div class="url-topic-ownership-v2-heading-tree">
              <div
                class="url-topic-ownership-v2-heading-tree-row"
                [class.url-topic-ownership-v2-heading-tree-row--compose-pick]="composeSelect"
                [class.url-topic-ownership-v2-heading-tree-row--compose-selected]="
                  composeSelect && isTopicOwnershipComposeHeadingSelected(v2Row, sc, depth)
                "
                [attr.role]="composeSelect ? 'button' : null"
                [attr.tabindex]="composeSelect ? 0 : null"
                [attr.aria-pressed]="
                  composeSelect ? isTopicOwnershipComposeHeadingSelected(v2Row, sc, depth) : null
                "
                (click)="composeSelect && toggleTopicOwnershipComposeHeading($event, v2Row, sc, depth)"
                (keydown.enter)="composeSelect && toggleTopicOwnershipComposeHeading($event, v2Row, sc, depth)"
                (keydown.space)="composeSelect && toggleTopicOwnershipComposeHeading($event, v2Row, sc, depth)"
                *ngFor="let seg of segments; let depth = index"
                [style.padding-left.rem]="(seg.treeDepth ?? depth) * 1.15"
              >
                <span
                  class="badge url-topic-ownership-v2-heading-level-chip"
                  *ngIf="topicOwnershipV2HeadingLevelChip(seg.level) as chip"
                  >{{ chip }}</span
                >
                <span
                  class="url-topic-ownership-v2-heading-path-segment"
                  [ngClass]="topicOwnershipV2SectionDisplayHeadingRole(seg)"
                  >{{ seg.heading }}</span
                >
              </div>
            </div>
          </ng-template>

          <ng-template #sectionComparePanel let-v2Row="row" let-composeSelect="composeSelect">
            <div class="url-topic-ownership-v2-detail url-topic-ownership-v2-heatmap url-topic-ownership-col-compare">
              <p
                class="url-topic-ownership-v2-listicle-note muted"
                *ngIf="topicOwnershipV2HasListInventory(v2Row) && (composeSelect || !isDevView())"
              >
                <ng-container *ngIf="composeSelect; else listicleNoteBrowse">
                  Roundup detected — pick missing <strong>tools</strong> above; section compare here is for
                  <strong>feature depth</strong> on tools you both cover.
                </ng-container>
                <ng-template #listicleNoteBrowse>
                  Roundup detected — open <strong>List inventory</strong> on the competitor row for
                  missing tools; section compare here is for feature depth.
                </ng-template>
              </p>
              <div class="url-topic-ownership-v2-compare-toolbar" *ngIf="isDevView() && !composeSelect">
                <p class="url-topic-ownership-v2-detail-lead">
                  <strong>What they cover that your page doesn’t</strong>
                  <span class="muted"> — their sections vs your whole focal page</span>
                </p>
                <div class="url-topic-ownership-v2-compare-filter" role="group" aria-label="Compare detail filter">
                  <button
                    type="button"
                    class="btn btn--sm btn--bare url-topic-ownership-v2-compare-filter-btn"
                    [class.url-topic-ownership-v2-compare-filter-btn--active]="
                      topicOwnershipV2CompareFilterMode() === 'not_on_page'
                    "
                    (click)="setTopicOwnershipV2CompareFilter('not_on_page')"
                  >
                    Not on your page
                  </button>
                  <button
                    type="button"
                    class="btn btn--sm btn--bare url-topic-ownership-v2-compare-filter-btn"
                    [class.url-topic-ownership-v2-compare-filter-btn--active]="
                      topicOwnershipV2CompareFilterMode() === 'gaps'
                    "
                    (click)="setTopicOwnershipV2CompareFilter('gaps')"
                  >
                    Gaps only
                  </button>
                  <button
                    type="button"
                    class="btn btn--sm btn--bare url-topic-ownership-v2-compare-filter-btn"
                    [class.url-topic-ownership-v2-compare-filter-btn--active]="
                      topicOwnershipV2CompareFilterMode() === 'all'
                    "
                    (click)="setTopicOwnershipV2CompareFilter('all')"
                  >
                    All sections
                  </button>
                </div>
              </div>

              <p
                class="muted url-topic-ownership-v2-compare-filter-empty"
                *ngIf="
                  isDevView() &&
                  !composeSelect &&
                  topicOwnershipV2CompareFilterMode() !== 'all' &&
                  !topicOwnershipV2FilteredSectionCoverageRows(v2Row).length
                "
              >
                {{ topicOwnershipV2CompareFilterEmptyMessage() }}
              </p>

              <div
                class="url-topic-ownership-v2-heatmap-section"
                [class.url-topic-ownership-v2-heatmap-section--missing-only]="!sc.sectionCovered"
                *ngFor="
                  let sc of composeSelect
                    ? topicOwnershipComposeSectionRows(v2Row)
                    : topicOwnershipV2FilteredSectionCoverageRows(v2Row)
                "
              >
                <div class="url-topic-ownership-v2-heatmap-section-top">
                  <ng-container *ngIf="isDevView(); else userSectionTopChips">
                    <span
                      class="badge url-topic-ownership-v2-page-chip"
                      *ngIf="topicOwnershipV2SectionPositionChip(sc)"
                      [title]="'Section ' + topicOwnershipV2SectionPositionChip(sc) + ' on competitor page'"
                      >{{ topicOwnershipV2SectionPositionChip(sc) }}</span
                    >
                    <span
                      class="url-topic-ownership-v2-section-status"
                      [ngClass]="topicOwnershipV2SectionStatusClass(sc)"
                      [title]="topicOwnershipV2SectionStatusTitle(sc)"
                      >{{ topicOwnershipV2SectionStatusLabel(sc) }}</span
                    >
                  </ng-container>
                  <ng-template #userSectionTopChips>
                    <span
                      class="url-topic-ownership-v2-section-status"
                      [ngClass]="topicOwnershipV2SectionStatusClass(sc)"
                      [title]="topicOwnershipV2SectionStatusTitle(sc)"
                      >{{ topicOwnershipV2SectionStatusLabel(sc) }}</span
                    >
                    <span
                      class="badge url-topic-ownership-v2-page-chip"
                      *ngIf="topicOwnershipV2SectionPositionChip(sc)"
                      [title]="'Section ' + topicOwnershipV2SectionPositionChip(sc) + ' on competitor page'"
                      >{{ topicOwnershipV2SectionPositionChip(sc) }}</span
                    >
                  </ng-template>
                </div>

                <div
                  class="url-topic-ownership-v2-section-heading-line"
                  *ngIf="!topicOwnershipV2SectionCompareExpanded(v2Row.competitorUrl, sc, composeSelect)"
                >
                  <span
                    class="badge url-topic-ownership-v2-page-chip"
                    *ngIf="isDevView()"
                    [title]="v2Row.competitorUrl"
                    >{{ topicOwnershipV2RowDomain(v2Row.competitorUrl) }}</span
                  >
                  <ng-container
                    *ngTemplateOutlet="
                      headingPathTree;
                      context: topicOwnershipHeadingTreeContext(v2Row, sc, composeSelect)
                    "
                  ></ng-container>
                </div>

                <p
                  class="url-topic-ownership-v2-missing-note url-topic-ownership-v2-non-topical-note"
                  *ngIf="isDevView() && sc.isNonTopical"
                >
                  {{ topicOwnershipV2NonTopicalStatusTitle(sc) }}
                </p>

                <p
                  class="url-topic-ownership-v2-missing-note"
                  *ngIf="isDevView() && !sc.sectionCovered && !sc.isNonTopical"
                >
                  Likely not covered on your page — no focal section reached the similarity threshold.
                </p>

                <div
                  class="url-topic-ownership-v2-missing-block"
                  *ngIf="!topicOwnershipV2SectionCompareExpanded(v2Row.competitorUrl, sc, composeSelect) && sc.sectionCovered && sc.absentTerms?.length"
                >
                  <h6 class="url-topic-ownership-v2-talking-heading">Phrases they cover, you don’t</h6>
                  <ul class="url-topic-ownership-v2-missing-only-gaps">
                    <li class="url-topic-ownership-v2-talking-point--missing" *ngFor="let term of sc.absentTerms">
                      {{ term }}
                    </li>
                  </ul>
                </div>

                <p
                  class="muted url-topic-ownership-v2-section-allcovered"
                  *ngIf="!composeSelect && sc.sectionCovered && !sc.absentTerms?.length && !sc.isNonTopical"
                >
                  You cover this section’s topic and its key phrases.
                </p>

                <button
                  type="button"
                  class="url-content-topics-expand url-topic-ownership-v2-fulltext-toggle"
                  *ngIf="!composeSelect && topicOwnershipV2SectionCompareAvailable(sc)"
                  (click)="toggleTopicOwnershipV2SectionFullText(v2Row.competitorUrl, sc); $event.stopPropagation()"
                >
                  {{
                    isTopicOwnershipV2SectionFullTextExpanded(v2Row.competitorUrl, sc)
                      ? 'Hide side-by-side compare'
                      : 'Show side-by-side compare'
                  }}
                </button>

                <div
                  class="url-topic-ownership-v2-compare-columns"
                  *ngIf="
                    topicOwnershipV2SectionCompareAvailable(sc) &&
                    topicOwnershipV2SectionCompareExpanded(v2Row.competitorUrl, sc, composeSelect)
                  "
                >
                  <div class="url-topic-ownership-v2-compare-col-stack">
                    <span
                      class="badge url-topic-ownership-v2-page-chip url-topic-ownership-v2-compare-above-chip"
                      [title]="v2Row.competitorUrl"
                      >{{ topicOwnershipV2RowDomain(v2Row.competitorUrl) }}</span
                    >
                    <div class="url-topic-ownership-v2-compare-col">
                      <div class="url-topic-ownership-v2-compare-col-title">
                        <ng-container
                          *ngTemplateOutlet="
                            headingPathTree;
                            context: topicOwnershipHeadingTreeContext(v2Row, sc, composeSelect)
                          "
                        ></ng-container>
                      </div>
                      <div
                        class="url-topic-ownership-v2-compare-body"
                        *ngIf="topicOwnershipV2SectionBodyText(sc); else compSectionEmpty"
                      >
                        <span
                          *ngFor="let seg of topicOwnershipV2SectionHighlightSegments(sc)"
                          [class.url-topic-ownership-v2-hl--missing]="seg.highlighted"
                          >{{ seg.text }}</span
                        >
                      </div>
                      <ng-template #compSectionEmpty>
                        <p class="muted url-topic-ownership-v2-compare-empty">No section body text.</p>
                      </ng-template>
                    </div>
                  </div>

                  <div class="url-topic-ownership-v2-compare-col-stack">
                    <span
                      class="badge url-topic-ownership-v2-page-chip url-topic-ownership-v2-compare-above-chip"
                      *ngIf="topicOwnershipV2CompareClosestLabel(sc) as closestLabel"
                      [title]="topicOwnershipV2ClosestMatchTitle(sc)"
                      >{{ closestLabel }}</span
                    >
                    <div class="url-topic-ownership-v2-compare-col">
                      <div class="url-topic-ownership-v2-compare-col-title">
                        <span class="badge url-topic-ownership-v2-page-chip" *ngIf="isDevView()" [title]="url || ''">{{
                          topicOwnershipV2FocalDomain()
                        }}</span>
                        <ng-container
                          *ngTemplateOutlet="
                            headingPathTree;
                            context: { $implicit: topicOwnershipV2CompareRightColumnSegments(sc) }
                          "
                        ></ng-container>
                      </div>
                      <div
                        class="url-topic-ownership-v2-compare-body"
                        *ngIf="topicOwnershipV2ClosestMyBodyText(sc); else mySectionEmpty"
                      >
                        {{ topicOwnershipV2ClosestMyBodyText(sc) }}
                      </div>
                      <ng-template #mySectionEmpty>
                        <p class="muted url-topic-ownership-v2-compare-empty">
                          {{
                            topicOwnershipV2HasClosestMatch(sc)
                              ? 'No body text in the closest focal section.'
                              : 'Nothing on your focal URL matched this section.'
                          }}
                        </p>
                      </ng-template>
                    </div>
                  </div>
                </div>
              </div>

              <span class="muted" *ngIf="v2Row.error"> — {{ v2Row.error }}</span>
            </div>
          </ng-template>

          <ng-container *ngIf="topicOwnershipComposing && topicOwnershipComposeState as composeState">
            <p class="plan-v2-compose-title url-topic-ownership-compose-title">What should we add?</p>

            <div class="url-topic-ownership-compose-block" *ngIf="topicOwnershipComposeHasPageGaps()">
              <h5 class="url-topic-ownership-compose-heading">Pages</h5>
              <p class="url-topic-ownership-compose-lead muted">
                Whole supporting URLs missing on your site — check any you want to add.
              </p>
              <div class="url-topic-ownership-compose-pages">
                <label class="url-topic-ownership-compose-page" *ngFor="let page of composeState.pageGaps">
                  <input type="checkbox" [(ngModel)]="page.selected" [name]="'compose-page-' + page.id" />
                  <span class="url-topic-ownership-compose-page-body">
                    <span class="url-topic-ownership-compose-page-path">
                      <strong>{{ page.competitorDomain }}</strong>{{ page.path }}
                    </span>
                    <span class="url-topic-ownership-compose-page-meta muted">{{ page.topicLabel }}</span>
                  </span>
                </label>
              </div>
            </div>

            <div class="url-topic-ownership-compose-block" *ngIf="topicOwnershipComposeHasToolGaps()">
              <h5 class="url-topic-ownership-compose-heading">Tools to cover</h5>
              <p class="url-topic-ownership-compose-lead muted">
                On their roundup but not on your page — check any you want to add.
              </p>
              <div
                class="url-topic-ownership-compose-competitor-pick"
                *ngIf="composeState.listCompetitors.length"
              >
                <ng-container *ngIf="composeState.listCompetitors.length > 1; else composeSingleListCompetitor">
                  <label class="url-topic-ownership-compare-label" for="topic-ownership-compose-list-competitor"
                    >Competitor roundup</label
                  >
                  <select
                    id="topic-ownership-compose-list-competitor"
                    class="input url-topic-ownership-compare-select"
                    [ngModel]="composeState.activeListCompetitorUrl"
                    (ngModelChange)="onTopicOwnershipComposeListCompetitorChange($event)"
                  >
                    <option *ngFor="let opt of composeState.listCompetitors" [value]="opt.url">
                      {{ opt.label }} ({{ opt.missingToolCount }} missing)
                    </option>
                  </select>
                </ng-container>
                <ng-template #composeSingleListCompetitor>
                  <p class="url-topic-ownership-compose-competitor-label muted">
                    From <strong>{{ composeState.listCompetitors[0].label }}</strong>
                    · {{ composeState.listCompetitors[0].missingToolCount }} missing tools
                  </p>
                </ng-template>
              </div>
              <div class="url-topic-ownership-compose-pages url-topic-ownership-compose-tools">
                <label class="url-topic-ownership-compose-page" *ngFor="let tool of topicOwnershipComposeToolsForActiveCompetitor()">
                  <input type="checkbox" [(ngModel)]="tool.selected" [name]="'compose-tool-' + tool.id" />
                  <span class="url-topic-ownership-compose-page-body">
                    <span class="url-topic-ownership-compose-page-path">{{ tool.label }}</span>
                  </span>
                </label>
              </div>
            </div>

            <div class="url-topic-ownership-compose-block" *ngIf="topicOwnershipComposeHasSectionGaps()">
              <h5 class="url-topic-ownership-compose-heading">Sections</h5>
              <div
                class="url-topic-ownership-compose-competitor-pick"
                *ngIf="composeState.sectionCompetitors.length"
              >
                <ng-container *ngIf="composeState.sectionCompetitors.length > 1; else composeSingleCompetitor">
                  <label class="url-topic-ownership-compare-label" for="topic-ownership-compose-competitor"
                    >Competitor page</label
                  >
                  <select
                    id="topic-ownership-compose-competitor"
                    class="input url-topic-ownership-compare-select"
                    [ngModel]="composeState.activeCompetitorUrl"
                    (ngModelChange)="onTopicOwnershipComposeCompetitorChange($event)"
                  >
                    <option *ngFor="let opt of composeState.sectionCompetitors" [value]="opt.url">
                      {{ opt.label }} ({{ opt.missingCount }} missing)
                    </option>
                  </select>
                </ng-container>
                <ng-template #composeSingleCompetitor>
                  <p class="url-topic-ownership-compose-competitor-label muted">
                    From <strong>{{ composeState.sectionCompetitors[0].label }}</strong>
                    · {{ composeState.sectionCompetitors[0].missingCount }} missing sections
                  </p>
                </ng-template>
              </div>
              <div
                class="url-topic-ownership-section-compare-fullrow url-topic-ownership-section-compare-fullrow--compose"
                *ngIf="topicOwnershipComposeActiveV2Row() as composeV2Row"
              >
                <ng-container
                  *ngTemplateOutlet="sectionComparePanel; context: { row: composeV2Row, composeSelect: true }"
                ></ng-container>
              </div>
            </div>
          </ng-container>

          <ng-container *ngIf="!topicOwnershipComposing">
          <!-- A legend teaching you how to read the table below. Deep-dive only:
               on a review surface the first line has to be the claim. -->
          <p
            class="url-topic-ownership-hint-user muted"
            *ngIf="supportingPagesColumnsReady() && !embedReviewSurface"
          >
            Top page per domain for this URL’s focal topics<ng-container *ngIf="topicOwnershipAnchorHint()"
              > ({{ topicOwnershipAnchorHint() }})</ng-container
            >.
            <span class="url-topic-ownership-coverage-legend url-topic-ownership-link--covered">Covered</span> ·
            <span class="url-topic-ownership-coverage-legend url-topic-ownership-link--gap">Gap</span> on competitor
            supporting URLs.
          </p>

          <div
            class="url-topic-ownership-skeleton"
            *ngIf="supportingPagesUnifiedLoading()"
            aria-busy="true"
            aria-label="Loading supporting pages"
          >
            <div class="url-topic-ownership-skeleton-columns">
              <div class="url-topic-ownership-skeleton-col" *ngFor="let _ of topicOwnershipSkeletonCols">
                <div class="url-topic-ownership-skeleton-title"></div>
                <div class="url-topic-ownership-skeleton-line" *ngFor="let __ of topicOwnershipSkeletonLines"></div>
              </div>
            </div>
          </div>

          <p
            class="url-topic-ownership-status muted"
            *ngIf="supportingPagesEmptyReady() && !supportingPagesColumnsReady()"
          >
            <ng-container *ngIf="topicOwnershipCacheMiss; else supportingPagesEmptyUnified">
              No precomputed snapshot yet — run <strong>Supporting pages by topic</strong> and
              <strong>Supporting page opportunities</strong> from Crawl Management.
            </ng-container>
            <ng-template #supportingPagesEmptyUnified>
              No supporting pages found for the strongest topics on this URL.
            </ng-template>
          </p>

          <p
            class="url-topic-ownership-status url-topic-ownership-v2-error"
            *ngIf="topicOwnershipV2Status === 'error'"
          >
            {{ topicOwnershipV2Error }}
            <button type="button" class="url-content-topics-expand" (click)="retryTopicOwnershipV2Test()">Retry</button>
          </p>

          <div
            class="url-topic-ownership-columns"
            *ngIf="supportingPagesColumnsReady() && !embedReviewSurface"
          >
            <div
              class="url-topic-ownership-col"
              *ngFor="let col of getSupportingPagesColumns()"
              [class.url-topic-ownership-col--you]="col.isYourSite"
            >
              <h5 class="url-topic-ownership-col-title">
                {{ col.domain }}
                <span
                  class="badge badge--accent"
                  *ngIf="col.serpSource === 'marked'"
                  title="Marked competitor on this SERP"
                >Marked</span>
              </h5>

              <p
                class="url-topic-ownership-your-site-empty muted"
                *ngIf="col.isYourSite && !col.supportingRows.length"
              >
                No other supporting pages on your site for this topic.
              </p>

              <div class="url-topic-ownership-col-hero" *ngIf="col.heroUrl">
                <a
                  class="url-topic-ownership-link url-topic-ownership-link--hero"
                  [href]="col.heroUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  [class.url-topic-ownership-link--current]="isCurrentPageSupportingUrl(col.heroUrl)"
                  [title]="topicOwnershipHeroLinkTitle(col)"
                >
                  <span class="url-topic-ownership-link-path">{{ topicOwnershipPagePath(col.heroUrl) }}</span
                  ><span
                    class="url-topic-ownership-link-traffic"
                    *ngIf="topicOwnershipPageTraffic(col.heroRow?.estimatedOrganicTraffic) as heroTraffic"
                  >
                    ({{ heroTraffic }})</span
                  >
                </a>

                <div class="url-topic-ownership-col-hero-topics" *ngIf="isDevView() && col.heroRow?.topics?.length">
                  <span
                    class="badge badge--pill"
                    *ngFor="let chip of col.heroRow!.topics"
                    [class.badge--success]="chip.role === 'main'"
                    [class.badge--warning]="chip.role === 'supporting'"
                    >{{ chip.topicLabel }}</span
                  >
                </div>

                <div class="url-topic-ownership-col-hero-chips" *ngIf="supportingPagesShowHeroChips(col)">
                  <span
                    class="badge url-topic-ownership-v2-page-chip url-topic-ownership-v2-serp-chip"
                    *ngIf="topicOwnershipV2ShowSerpRankChip(col.heroUrl)"
                    [ngClass]="topicOwnershipV2SerpRankChipClass(col.heroUrl)"
                    [title]="topicOwnershipV2SerpRankChipTitle(col.heroUrl)"
                    >{{ topicOwnershipV2SerpRankChipLabel(col.heroUrl) }}</span
                  >
                  <ng-container *ngIf="col.v2Row as v2Row">
                  <button
                    type="button"
                    class="btn btn--sm url-topic-ownership-v2-list-inventory-chip"
                    *ngIf="topicOwnershipV2HasListInventory(v2Row)"
                    [class.url-topic-ownership-v2-list-inventory-chip--open]="isTopicOwnershipV2ListInventoryOpen(col)"
                    [attr.aria-expanded]="isTopicOwnershipV2ListInventoryOpen(col)"
                    [title]="topicOwnershipV2ListInventoryChipTitle(v2Row)"
                    (click)="toggleTopicOwnershipV2ListInventory(v2Row.competitorUrl)"
                  >
                    <span
                      class="url-topic-ownership-v2-chip-chevron"
                      [class.url-topic-ownership-v2-chip-chevron--open]="isTopicOwnershipV2ListInventoryOpen(col)"
                      aria-hidden="true"
                      >▼</span
                    >
                    <span class="url-topic-ownership-v2-chip-label">{{
                      topicOwnershipV2ListInventoryChipLabel(v2Row)
                    }}</span>
                  </button>
                  <button
                    type="button"
                    class="btn btn--sm btn--warning url-topic-ownership-v2-rank-driven-chip"
                    *ngIf="topicOwnershipV2HasRankDrivenGaps(v2Row)"
                    [class.url-topic-ownership-v2-rank-driven-chip--open]="isTopicOwnershipV2RankDrivenOpen(col)"
                    [attr.aria-expanded]="isTopicOwnershipV2RankDrivenOpen(col)"
                    [title]="topicOwnershipV2RankDrivenChipTitle(v2Row)"
                    (click)="toggleTopicOwnershipV2RankDriven(v2Row.competitorUrl)"
                  >
                    <span
                      class="url-topic-ownership-v2-chip-chevron"
                      [class.url-topic-ownership-v2-chip-chevron--open]="isTopicOwnershipV2RankDrivenOpen(col)"
                      aria-hidden="true"
                      >▼</span
                    >
                    <span class="url-topic-ownership-v2-chip-label">{{
                      topicOwnershipV2RankDrivenChipLabel(v2Row)
                    }}</span>
                  </button>
                  <button
                    type="button"
                    class="btn btn--sm btn--danger url-topic-ownership-v2-missing-chip"
                    *ngIf="topicOwnershipV2HasMissingSections(v2Row) && topicOwnershipV2HasSectionCoverage(v2Row)"
                    [class.url-topic-ownership-v2-missing-chip--open]="isTopicOwnershipV2CompareOpen(col)"
                    [attr.aria-expanded]="isTopicOwnershipV2CompareOpen(col)"
                    (click)="toggleTopicOwnershipV2Detail(v2Row.competitorUrl)"
                  >
                    <span
                      class="url-topic-ownership-v2-chip-chevron"
                      [class.url-topic-ownership-v2-chip-chevron--open]="isTopicOwnershipV2CompareOpen(col)"
                      aria-hidden="true"
                      >▼</span
                    >
                    <span class="url-topic-ownership-v2-chip-label">{{
                      topicOwnershipV2MissingSectionsChipLabel(v2Row)
                    }}</span>
                  </button>
                  <button
                    type="button"
                    class="btn btn--sm url-topic-ownership-v2-covered-chip"
                    [class.url-topic-ownership-v2-covered-chip--open]="isTopicOwnershipV2CoveredElsewhereOpen(col)"
                    [attr.aria-expanded]="isTopicOwnershipV2CoveredElsewhereOpen(col)"
                    (click)="toggleTopicOwnershipV2CoveredElsewhere(v2Row.competitorUrl)"
                  >
                    <span
                      class="url-topic-ownership-v2-chip-chevron"
                      [class.url-topic-ownership-v2-chip-chevron--open]="isTopicOwnershipV2CoveredElsewhereOpen(col)"
                      aria-hidden="true"
                      >▼</span
                    >
                    <span class="url-topic-ownership-v2-chip-label">{{
                      topicOwnershipV2CoveredElsewhereChipLabel(v2Row)
                    }}</span>
                  </button>
                  </ng-container>
                </div>
              </div>

              <div
                class="card card--pad card--edge card--warning url-topic-ownership-rank-driven-card"
                *ngIf="!col.isYourSite && col.v2Row && isTopicOwnershipV2RankDrivenOpen(col)"
              >
                <p class="url-topic-ownership-rank-driven-lead">
                  Keywords they rank for that you miss — with on-page passages
                </p>
                <ul class="url-topic-ownership-rank-driven-items">
                  <li
                    *ngFor="let gap of topicOwnershipV2RankDrivenGaps(col.v2Row)"
                    class="url-topic-ownership-rank-driven-item"
                  >
                    <div class="url-topic-ownership-rank-driven-main">
                      <span class="badge badge--warning">rank-driven</span>
                      {{ gap.evidenceLine }}
                    </div>
                    <div class="url-rank-driven-passage" *ngIf="gap.rivalPassageHeading || gap.rivalPassage">
                      <span class="url-rank-driven-passage-label">Their page</span>
                      <div class="url-rank-driven-passage-heading-row" *ngIf="gap.rivalPassageHeading">
                        <span
                          class="url-rank-driven-heading-tag"
                          *ngIf="rankDrivenHeadingTag(gap.rivalPassageHeadingLevel, gap.rivalPassageKind)"
                        >
                          {{ rankDrivenHeadingTag(gap.rivalPassageHeadingLevel, gap.rivalPassageKind) }}
                        </span>
                        <p class="url-rank-driven-passage-heading">
                          <ng-container
                            *ngFor="
                              let seg of rankDrivenPassageSegments(gap.keyword, gap.rivalPassageHeading)
                            "
                          >
                            <mark *ngIf="seg.highlight">{{ seg.text }}</mark>
                            <ng-container *ngIf="!seg.highlight">{{ seg.text }}</ng-container>
                          </ng-container>
                        </p>
                      </div>
                      <p class="url-rank-driven-passage-text" *ngIf="gap.rivalPassage">
                        <ng-container
                          *ngFor="let seg of rankDrivenPassageSegments(gap.keyword, gap.rivalPassage)"
                        >
                          <mark *ngIf="seg.highlight">{{ seg.text }}</mark>
                          <ng-container *ngIf="!seg.highlight">{{ seg.text }}</ng-container>
                        </ng-container>
                      </p>
                    </div>
                    <div class="url-rank-driven-passage url-rank-driven-passage--yours">
                      <span class="url-rank-driven-passage-label">Your page</span>
                      <div class="url-rank-driven-passage-heading-row" *ngIf="gap.focalPassageHeading">
                        <span
                          class="url-rank-driven-heading-tag"
                          *ngIf="rankDrivenHeadingTag(gap.focalPassageHeadingLevel, gap.focalPassageKind)"
                        >
                          {{ rankDrivenHeadingTag(gap.focalPassageHeadingLevel, gap.focalPassageKind) }}
                        </span>
                        <p class="url-rank-driven-passage-heading">
                          <ng-container
                            *ngFor="
                              let seg of rankDrivenPassageSegments(gap.keyword, gap.focalPassageHeading)
                            "
                          >
                            <mark *ngIf="seg.highlight">{{ seg.text }}</mark>
                            <ng-container *ngIf="!seg.highlight">{{ seg.text }}</ng-container>
                          </ng-container>
                        </p>
                      </div>
                      <p class="url-rank-driven-passage-text" *ngIf="gap.focalPassage">
                        <ng-container
                          *ngFor="let seg of rankDrivenPassageSegments(gap.keyword, gap.focalPassage)"
                        >
                          <mark *ngIf="seg.highlight">{{ seg.text }}</mark>
                          <ng-container *ngIf="!seg.highlight">{{ seg.text }}</ng-container>
                        </ng-container>
                      </p>
                      <p
                        class="url-rank-driven-passage-missing muted"
                        *ngIf="!gap.focalPassage && !gap.focalPassageHeading"
                      >
                        No matching passage for this keyword.
                      </p>
                    </div>
                    <p
                      class="url-rank-driven-mismatch"
                      *ngIf="gap.queryFamilyMismatch && gap.mismatchNote"
                    >
                      {{ gap.mismatchNote }}
                    </p>
                  </li>
                </ul>
              </div>

              <div
                class="card card--pad card--edge card--info url-topic-ownership-list-inventory-card"
                *ngIf="!col.isYourSite && col.v2Row && isTopicOwnershipV2ListInventoryOpen(col)"
              >
                <p class="url-topic-ownership-list-inventory-lead">
                  On their roundup but not mentioned on your page
                </p>
                <ul class="url-topic-ownership-list-inventory-items">
                  <li *ngFor="let item of col.v2Row.listInventoryDiff?.competitorOnly">
                    {{ item.label }}
                  </li>
                </ul>
                <p
                  class="muted url-topic-ownership-list-inventory-meta"
                  *ngIf="col.v2Row.listInventoryDiff?.onBoth?.length"
                >
                  {{ col.v2Row.listInventoryDiff!.onBoth!.length }} shared ·
                  {{ col.v2Row.listInventoryDiff!.competitorItemCount }} on theirs ·
                  {{ col.v2Row.listInventoryDiff!.focalItemCount }} on yours
                </p>
                <p class="muted url-topic-ownership-list-inventory-note" *ngIf="!isDevView()">
                  Roundup vs roundup — use this for missing <strong>tools</strong>. Section compare
                  below is for <strong>feature depth</strong> on tools you both cover.
                </p>
              </div>

              <div
                class="card card--pad url-topic-ownership-covered-card"
                *ngIf="!col.isYourSite && col.v2Row && isTopicOwnershipV2CoveredElsewhereOpen(col)"
              >
                <ng-container *ngIf="col.v2Row.coveredElsewhereUrl; else coveredElsewhereEmpty">
                  <p class="url-topic-ownership-covered-card-lead muted">Similar page on your site</p>
                  <a
                    class="url-topic-ownership-link url-topic-ownership-covered-card-link"
                    [href]="col.v2Row.coveredElsewhereUrl"
                    target="_blank"
                    rel="noopener noreferrer"
                    >{{ topicOwnershipPagePath(col.v2Row.coveredElsewhereUrl) }}</a
                  >
                  <p class="url-topic-ownership-covered-card-meta muted" *ngIf="col.v2Row.coveredElsewhereLabel">
                    {{ col.v2Row.coveredElsewhereLabel }}
                  </p>
                </ng-container>
                <ng-template #coveredElsewhereEmpty>
                  <p class="muted url-topic-ownership-covered-card-empty">
                    No similar supporting page found elsewhere on your site for this competitor URL.
                  </p>
                </ng-template>
              </div>

              <ul class="url-topic-ownership-col-links" *ngIf="col.supportingRows.length">
                <li *ngFor="let row of getVisibleSupportingRows(col)">
                  <a
                    class="url-topic-ownership-link"
                    [href]="row.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    [class.url-topic-ownership-link--current]="isCurrentPageSupportingUrl(row.url)"
                    [class.url-topic-ownership-link--covered]="!col.isYourSite && row.similarOnYourSite"
                    [class.url-topic-ownership-link--gap]="!col.isYourSite && !row.similarOnYourSite"
                    [title]="topicOwnershipSupportingLinkTitle(col, row)"
                  >
                    <span class="url-topic-ownership-link-path">{{ topicOwnershipPagePath(row.url) }}</span
                    ><span
                      class="url-topic-ownership-link-traffic"
                      *ngIf="topicOwnershipPageTraffic(row.estimatedOrganicTraffic) as traffic"
                    >
                      ({{ traffic }})</span
                    >
                  </a>
                  <div class="url-topic-ownership-col-row-topics" *ngIf="isDevView() && row.topics?.length">
                    <span
                      class="badge badge--pill"
                      *ngFor="let chip of row.topics"
                      [class.badge--success]="chip.role === 'main'"
                      [class.badge--warning]="chip.role === 'supporting'"
                      >{{ chip.topicLabel }}</span
                    >
                  </div>
                </li>
              </ul>

              <button
                type="button"
                class="url-content-topics-expand url-topic-ownership-expand"
                *ngIf="hasMoreSupportingRowsInColumn(col) || isTopicOwnershipPagesExpanded(col.domain)"
                (click)="toggleTopicOwnershipPagesExpanded(col.domain)"
                [attr.aria-expanded]="isTopicOwnershipPagesExpanded(col.domain)"
              >
                {{ supportingRowsExpandLabel(col) }}
              </button>
            </div>
          </div>

          <!-- ── The review surface's version of the same card ──────────────
               One question: what closely related pages do they have that I
               don't? The verdict is the first line rather than a legend, and
               what is left under it is a list of pages to write. Rules and
               reasoning in `utils/review-supporting-pages`. -->
          <p class="url-focus-pending muted" *ngIf="reviewSupportingPagesPending()">
            Comparing against the rival ranking for this page…
          </p>

          <div
            class="review-gaps"
            *ngIf="embedReviewSurface && supportingPagesColumnsReady()"
          >
            <div class="review-gaps__col" *ngFor="let col of reviewSupportingPagesColumns()">
              <p class="review-gaps__verdict">{{ col.verdict }}</p>
              <ul class="review-gaps__list">
                <!-- A tick per page. Wanting one of two pages in the next
                     calendar is the ordinary case, and the card tick could only
                     say "both" or "neither". -->
                <li
                  class="review-gaps__row review-gaps__row--tickable"
                  [class.review-gaps__row--unticked]="
                    !reviewCardSending('topic_ownership') || !isSupportingPageTicked(gap.url)
                  "
                  *ngFor="let gap of col.gaps"
                >
                  <input
                    class="review-gaps__tick"
                    type="checkbox"
                    [checked]="reviewCardSending('topic_ownership') && isSupportingPageTicked(gap.url)"
                    [attr.aria-label]="'Write ' + gap.path"
                    (change)="toggleSupportingPage(gap.url, $event)"
                  />
                  <a
                    class="review-gaps__link"
                    [href]="gap.url"
                    target="_blank"
                    rel="noopener noreferrer"
                    >{{ gap.path }}</a
                  ><span class="review-gaps__traffic" *ngIf="gap.traffic"> ({{ gap.traffic }})</span>
                </li>
              </ul>
            </div>

            <!-- Said only once everything has settled, like every other claim
                 on this surface: "nothing missing" that turns into a gap four
                 seconds later is the worst flip of the lot. -->
            <p class="review-gaps__none" *ngIf="reviewSupportingPagesEmpty()">
              Nothing on this topic that they have and you don’t.
            </p>
          </div>

          <!-- Sections, rank gaps and internal links used to render here, under
               this card's single tick. They are three different jobs — write
               new pages, deepen this one, edit other pages — and one tick over
               all of them meant an operator who wanted the sections and not the
               pages had no way to say so. Sections now have their own card
               below; internal links already had their own, and rendering them
               here as well was showing the same suggestions twice. -->

          <div class="url-topic-ownership-section-compare-fullrow" *ngIf="getOpenSectionCompareV2Row() as openCompareRow">
            <ng-container
              *ngTemplateOutlet="sectionComparePanel; context: { row: openCompareRow }"
            ></ng-container>
          </div>

          <!-- The compare target is chosen for the operator, and the dropdown
               below it is the override. Picking the rival was the fifth of the
               five acts §1 counts, and the one they are least placed to make:
               knowing which of a rival's pages is the real peer is what the
               resolver already computes. -->
          <!-- Both compare controls are deep-dive only. On the review surface the
               conclusion is pulled in instead — offering a tool where an answer
               belongs is what this pass is removing. The wrapper is a container
               rather than a second condition on each `*ngIf` so that
               `compareDefaultTarget() as target` keeps binding the target and
               not the result of an `&&`. -->
          <ng-container *ngIf="!embedReviewSurface">
          <div class="url-topic-ownership-compare" *ngIf="compareDefaultTarget() as target">
            <div class="url-compare-default">
              <button type="button" class="btn btn--accent btn--sm" (click)="openCompareDefault()">
                Compare vs {{ target.domain }}
              </button>
              <p class="url-compare-default-why">{{ compareDefaultReason() }}</p>
            </div>
          </div>

          <div class="url-topic-ownership-compare" *ngIf="topicOwnershipCompareOptions().length">
            <label class="url-topic-ownership-compare-label" for="topic-ownership-compare-select">
              {{ compareDefaultTarget() ? 'Or compare to…' : 'Compare to…' }}
            </label>
            <select
              id="topic-ownership-compare-select"
              class="input url-topic-ownership-compare-select"
              [(ngModel)]="topicOwnershipCompareUrl"
              (ngModelChange)="onTopicOwnershipCompareSelected($event)"
            >
              <option value="">Select competitor page</option>
              <option *ngFor="let opt of topicOwnershipCompareOptions()" [value]="opt.url">{{ opt.label }}</option>
            </select>
          </div>
          </ng-container>
          </ng-container>
          <app-plan-v2-issue-card-actions
            [reviewSelect]="embedReviewSurface"
            [reviewPending]="reviewSupportingPagesPending()"
            *ngIf="showIssueCardActions(topicPayload)"
            [payload]="topicPayload"
            [domain]="planV2IssueDomain()"
            (composingChange)="onTopicOwnershipComposingChange($event)"
          />
        </article>

        <!-- Blank for a reason, and the reason is not about this page (§0at).
             Sits where the Missing sections card would be, so it is read in
             the slot the operator was already looking at. -->
        <article
          *ngIf="reviewSectionCompareNotice() as compareNotice"
          class="card card--pad url-focus-card"
        >
          <div class="url-focus-card-top">
            <h4 class="url-focus-card-title">Missing sections</h4>
            <span class="badge badge--caps" [ngClass]="diagnosisChipClass('muted')">{{
              compareNotice.kind === 'blocked' ? 'NOT READABLE' : 'RETRYING'
            }}</span>
          </div>
          <p class="url-serp-intent-brief muted">{{ compareNotice.text }}</p>
        </article>

        <!-- ── Missing sections ─────────────────────────────────────────────
             Split out of the card above, where it shared a tick with "pages to
             write". They are different work — one is a brief for a new page,
             the other is an edit to this one — they land in different places on
             a content calendar, and an operator routinely wants one and not the
             other. Review surface only: on Inventory the topic card answers the
             whole question at once and that density is the point there. -->
        <article
          *ngIf="buildSectionGapIssuePayload() as sectionPayload"
          class="card card--pad url-focus-card url-focus-card-section-gaps"
          [ngClass]="planV2CardExitClass(sectionPayload.issueKey)"
        >
          <div class="url-focus-card-top">
            <!-- Named from the rows: a card where every row says "you have
                 this, it is thin" must not be titled Missing sections. -->
            <h4 class="url-focus-card-title">{{ reviewGapsCardTitle() }}</h4>
            <span
              class="badge badge--caps"
              *ngIf="reviewMissingSectionsChipLabel() as sectionChip"
              [ngClass]="diagnosisChipClass('warn')"
              >{{ sectionChip }}</span
            >
            <span class="url-focus-checking muted" *ngIf="reviewSupportingPagesPending()">Checking…</span>
          </div>

          <!-- What the top result covers and this page does not. -->
          <div class="review-gaps" *ngIf="reviewMissingSections() as missing">
            <div class="review-gaps__col">
              <!-- The rival's page, not their domain: what an operator wants to
                   see is the article that outranks them. New tab, because
                   leaving the belt loses your place in it. -->
              <p class="review-gaps__verdict">
                <a
                  class="review-gaps__rival"
                  *ngIf="missing.rivalUrl as rival; else plainName"
                  [href]="rival"
                  target="_blank"
                  rel="noopener noreferrer"
                  >{{ missing.rivalLabel }}</a
                >
                <ng-template #plainName>{{ missing.rivalLabel }}</ng-template
                >{{ missing.verdictRest }}
              </p>
              <ul class="review-gaps__list">
                <li
                  class="review-gaps__row review-gaps__row--tickable review-gaps__row--stacked"
                  [class.review-gaps__row--unticked]="
                    !reviewCardSending('section_gap') || !isMissingSectionTicked(row.id)
                  "
                  *ngFor="let row of missing.rows"
                >
                  <input
                    class="review-gaps__tick"
                    type="checkbox"
                    [checked]="reviewCardSending('section_gap') && isMissingSectionTicked(row.id)"
                    [attr.aria-label]="'Add ' + row.label"
                    (change)="toggleMissingSection(row.id, $event)"
                  />
                  <span class="review-gaps__section">
                    <!-- These rows are the rival's HEADINGS, and a run of plain
                         text under a checkbox read as a list someone typed. The
                         level says whether you are being asked for a section or
                         for a sub-point inside one. -->
                    <span class="review-gaps__level" *ngIf="row.level as level">H{{ level }}</span
                    >{{ row.label }}
                  </span>
                  <!-- The shape of the work. "Write one" and "expand the one you
                       have" are different asks, different effort, and until this
                       line they looked identical on the belt. -->
                  <span class="review-gaps__kind" [class.review-gaps__kind--deepen]="row.kind === 'deepen'">
                    <ng-container *ngIf="row.kind === 'write'">New section</ng-container>
                    <!-- "Shallow coverage" rather than a new phrase: the
                         rank-gap rows in this same card already say it, and one
                         card with two wordings for one idea makes a reader stop
                         and work out whether they mean different things. -->
                    <ng-container *ngIf="row.kind === 'deepen'">
                      Shallow coverage<ng-container *ngIf="row.closest as near">
                        — closest: {{ near.heading }} ({{ near.pct }}%)</ng-container
                      >
                    </ng-container>
                  </span>
                  <!-- The pieces a grouped gap collapsed. One decision, but
                       three things to write, and the parent heading alone hides
                       both the size and the shape of the work. -->
                  <span class="review-gaps__members" *ngIf="row.members.length">
                    <span class="review-gaps__member" *ngFor="let member of row.members">
                      <span class="review-gaps__level" *ngIf="member.level as level">H{{ level }}</span
                      >{{ member.heading }}
                    </span>
                  </span>
                  <!-- What they actually put in it, in their words. Answers the
                       question the heading provokes without a second screen. -->
                  <span class="review-gaps__excerpt" *ngIf="row.excerpt as excerpt"
                    >“{{ excerpt }}”</span
                  >
                  <!-- The vocabulary that made this a gap rather than a heading
                       you happen not to have — the same terms Compare shows. -->
                  <span class="review-gaps__terms" *ngIf="row.absentTerms.length">
                    <span class="review-gaps__terms-label">They cover</span
                    ><span class="review-gaps__term" *ngFor="let term of row.absentTerms">{{ term }}</span>
                  </span>
                </li>
              </ul>
            </div>
          </div>

          <!-- Queries a rival outranks you on with ground you already cover.
               Same card because the fix is the same fix: deepen this page. -->
          <div class="review-gaps" *ngIf="reviewRankGaps() as rank">
            <div class="review-gaps__col">
              <!-- The rival's page, same as the section-gap verdict above. -->
              <p class="review-gaps__verdict">
                <a
                  class="review-gaps__rival"
                  *ngIf="rank.rivalUrl as rival; else plainRankName"
                  [href]="rival"
                  target="_blank"
                  rel="noopener noreferrer"
                  >{{ rank.rivalLabel }}</a
                >
                <ng-template #plainRankName>{{ rank.rivalLabel }}</ng-template
                >{{ rank.verdictRest }}
              </p>
              <ul class="review-gaps__list">
                <li class="review-gaps__row review-gaps__row--stacked" *ngFor="let row of rank.rows">
                  <!-- Section first: it is the work. The queries below are the
                       reason it is worth doing, not the finding itself. -->
                  <span class="review-gaps__section" *ngIf="row.section">{{ row.section }}</span>
                  <span class="review-gaps__evidence">{{ row.evidence }}</span>
                  <span class="review-gaps__demand" *ngIf="row.demand">{{ row.demand }}</span>
                  <span class="review-gaps__coverage" *ngIf="row.coverage">{{ row.coverage }}</span>
                  <!-- Folded variants, named rather than dropped: three
                       spellings of one 260/mo query are one decision. -->
                  <span class="review-gaps__variants" *ngIf="row.variants">{{ row.variants }}</span>
                </li>
              </ul>
            </div>
          </div>

          <app-plan-v2-issue-card-actions
            [reviewSelect]="embedReviewSurface"
            [reviewPending]="reviewSupportingPagesPending()"
            *ngIf="showIssueCardActions(sectionPayload)"
            [payload]="sectionPayload"
            [domain]="planV2IssueDomain()"
          />
        </article>
      </div>

      <ng-template #topicCompetitorsCell let-signal="signal">
        <ng-container [ngSwitch]="competitorCoverageStatus">
          <span *ngSwitchCase="'loading'" class="competitor-state muted">Loading…</span>
          <span *ngSwitchCase="'error'" class="competitor-state muted">{{ competitorCoverageMessage || 'Unavailable' }}</span>
          <ng-container *ngSwitchCase="'ready'">
            <div class="competitor-pills" *ngIf="hasCompetitorMatches(signal); else noCompetitorsForTopic">
              <span
                class="competitor-pill-wrap"
                *ngFor="let match of getCompetitorTopicMatches(signal)"
                (mouseenter)="onCompetitorPillHover(match)"
                (mouseleave)="onCompetitorPillLeave()"
              >
                <span class="badge badge--pill">{{ formatCompetitorPill(match) }}</span>
                <span
                  class="competitor-popover"
                  *ngIf="isCompetitorTooltipVisible(match)"
                  (mouseenter)="onCompetitorPopoverEnter(match)"
                  (mouseleave)="onCompetitorPopoverLeave()"
                >
                  <ng-container [ngSwitch]="getCompetitorTooltipState(match).status">
                    <span *ngSwitchCase="'loading'">Loading top URLs…</span>
                    <span *ngSwitchCase="'error'">No URL-level preview available.</span>
                    <span *ngSwitchDefault>
                      <span class="competitor-popover-title">{{ match.domain }}</span>
                      <table class="table table--dense">
                        <thead>
                          <tr>
                            <th class="col-url">Page</th>
                            <th class="table__num col-num">Relevance</th>
                            <th class="table__num col-num">Salience</th>
                            <th class="table__num col-num">Traffic/mo</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr
                            *ngFor="let item of getCompetitorTooltipState(match).urls"
                            class="competitor-popover-row"
                          >
                            <td class="col-url">
                              <a
                                href=""
                                class="url"
                                (click)="onCompetitorPopoverUrlClick(match, item.url, $event)"
                                [title]="'Open Quick Compare with ' + item.url"
                              >{{ item.url }}</a>
                            </td>
                            <td class="table__num score">{{ item.score | number:'1.2-2' }}</td>
                            <td class="table__num score">{{ item.salience != null ? (item.salience | number:'1.2-2') : '—' }}</td>
                            <td class="table__num tooltip-url-traffic">{{ item.estimatedOrganicTraffic != null ? (item.estimatedOrganicTraffic | number:'1.0-0') : '—' }}</td>
                          </tr>
                        </tbody>
                      </table>
                    </span>
                  </ng-container>
                </span>
              </span>
            </div>
            <ng-template #noCompetitorsForTopic>
              <span class="competitor-state muted">—</span>
            </ng-template>
          </ng-container>
          <span *ngSwitchDefault class="competitor-state muted">—</span>
        </ng-container>
      </ng-template>
    </section>

    <section class="url-focus-row" aria-labelledby="url-freshness-heading" *ngIf="showEmbedFreshnessSection()">
      <h3 id="url-freshness-heading" class="url-focus-row-heading">Freshness</h3>
      <div class="url-focus-row-cards url-focus-row-cards--freshness">
        <article
          class="card card--pad url-focus-card url-focus-card-freshness"
          *ngIf="buildFreshnessIssuePayload() as freshnessPayload"
          [ngClass]="planV2CardExitClass(freshnessPayload.issueKey)"
        >
          <div class="url-focus-card-top">
            <h4 class="url-focus-card-title">Last update</h4>
            <span class="badge badge--caps" [ngClass]="diagnosisChipClass(freshnessDiagnosisTone())">{{ freshnessDiagnosisLabel() }}</span>
          </div>
          <dl class="url-focus-dl">
            <div class="url-focus-dl-row">
              <dt>Your page</dt>
              <dd>{{ pageContentLastModifiedLabel() }}</dd>
            </div>
            <div class="url-focus-dl-row" *ngIf="serpWinnersPublishedDateSummary() as serpDates">
              <dt>Top SERP ({{ serpDates.count }} dated)</dt>
              <dd>{{ serpDates.typicalLabel }}</dd>
            </div>
          </dl>
          <p class="url-focus-card-note muted" *ngIf="contentFreshnessVerdict() as verdict">{{ verdict }}</p>
          <app-plan-v2-issue-card-actions
            [reviewSelect]="embedReviewSurface"
            [reviewPending]="reviewSupportingPagesPending()"
            *ngIf="showIssueCardActions(freshnessPayload)"
            [payload]="freshnessPayload"
            [domain]="planV2IssueDomain()"
          />
        </article>
      </div>
    </section>

    <!-- Two states that must not read alike: a page with nothing wrong, and a
         page whose findings the operator already set aside. "Everything looks
         clear" over the second one is a lie the operator told themselves. -->
    <p class="url-focus-embed-empty muted" *ngIf="embedActionsOnlyEmpty()">
      <ng-container *ngIf="embedEmptyBecauseDismissed(); else nothingWrong">
        Every finding on this page was set aside earlier. Restore it from
        Inventory → Living brief → Show hidden if you want it back.
      </ng-container>
      <ng-template #nothingWrong>
        No actionable findings on these checks — everything looks clear.
      </ng-template>
    </p>

    <section class="url-focus-row url-focus-row-serp" *ngIf="showEmbedSerpSection()" aria-labelledby="url-serp-heading">
      <div class="url-focus-row-heading-bar">
        <h3 id="url-serp-heading" class="url-focus-row-heading">SERP &amp; INTENT</h3>
        <div class="url-serp-row-toolbar">
          <!-- Expands the full SERP results table. Reading the top ten is the
               deep dive's job; on the belt it is one more thing to click
               instead of decide. -->
          <button
            type="button"
            class="btn btn--sm"
            (click)="toggleSerpSectionExpanded()"
            [attr.aria-expanded]="serpSectionExpanded"
            *ngIf="hasPageSerpSnapshot() && !embedReviewSurface"
          >{{ serpSectionExpandLabel() }}</button>
          <!-- A control that re-fetches is an invitation to stop deciding and
               start investigating, which is the mode switch the review surface
               exists to avoid. Off here even in dev view: the operator walking
               the belt has dev view on, and "kept in dev" put the tools back on
               the one screen that is supposed to be free of them. -->
          <div class="url-serp-refresh-actions" *ngIf="!embedReviewSurface">
            <button
              type="button"
              class="btn btn--sm"
              (click)="refreshUrlSerpForDomain()"
              [disabled]="urlSerpRefreshRunning"
            >{{ urlSerpRefreshRunning ? 'Refreshing…' : 'Refresh SERP' }}</button>
            <label class="url-serp-force-label" title="Bypass SERP and keyword-difficulty caches; re-fetch from API.">
              <input type="checkbox" [(ngModel)]="urlSerpForceRefresh" [disabled]="urlSerpRefreshRunning" />
              Force API
            </label>
          </div>
        </div>
      </div>
      <!-- Which query, fetched when, classified how. Provenance for someone
           auditing the snapshot; on the belt it is three facts to read past
           before the first decision, and the keyword is already in the page
           header above. -->
      <p class="url-focus-row-meta muted" *ngIf="hasPageSerpSnapshot() && !embedReviewSurface">
        {{ pageSerpTargetQueryLabel() }} · {{ pageSerpFetchedLabel() }} · {{ pageSerpQuerySourceLabel() }}
      </p>
      <p class="url-claim-override-notice muted" *ngIf="claimOverrideNotice">{{ claimOverrideNotice }}</p>
      <p class="url-serp-override-loading muted" *ngIf="claimOverrideSaving">Refreshing SERP and intent…</p>
      <p class="muted" *ngIf="!hasPageSerpSnapshot()">{{ pageSerpPendingReason() }}</p>

      <div
        class="url-serp-inline-progress"
        *ngIf="urlSerpRefreshRunning && urlSerpRefreshProgress"
        role="status"
        aria-live="polite"
      >
        <div class="url-serp-inline-progress-bar">
          <div class="url-serp-inline-progress-fill" [style.width.%]="urlSerpRefreshProgress.percent"></div>
        </div>
        <p class="url-serp-inline-progress-line">{{ urlSerpRefreshProgress.statusLine }}</p>
        <p class="url-serp-inline-progress-eta muted" *ngIf="urlSerpRefreshProgress.etaLabel">{{ urlSerpRefreshProgress.etaLabel }}</p>
      </div>
      <p class="url-focus-card-note muted url-serp-scope-hint" *ngIf="isDevView() && !embedReviewSurface">
        Refresh updates <strong>this URL’s target query only</strong>. Use Inventory → Refresh URL SERP for the full domain.
      </p>

      <!-- The rank line lived here and went with the rest of the preamble. It
           was orientation rather than a decision, and the header above already
           carries the page's traffic and its gap. If it comes back it belongs
           up there beside them, not as a fourth line of preamble here. -->

      <div class="url-serp-cards-row">
        <ng-container *ngIf="showEmbedSerpCompetitionCard() && serpCompetitionInsight() as competition">
        <article
          class="card card--pad url-focus-card url-serp-subcard url-serp-subcard-competition"
          *ngIf="buildSerpCompetitionIssuePayload() as serpCompPayload"
          [ngClass]="planV2CardExitClass(serpCompPayload.issueKey)"
        >
          <div class="url-focus-card-top">
            <h4 class="url-focus-card-title">Who’s beating you?</h4>
            <span class="badge badge--caps" [ngClass]="diagnosisChipClass(serpCompetitionChipTone())">{{
              serpCompetitionChipLabel()
            }}</span>
          </div>
          <dl class="url-focus-dl url-serp-competition-summary">
            <div class="url-focus-dl-row">
              <dt>Your rank</dt>
              <dd>{{ serpCompetitionYourRankLabel() }}</dd>
            </div>
            <div class="url-focus-dl-row" *ngIf="markedCompetitorDomains.length">
              <dt>Rival ahead (top 5)</dt>
              <dd>{{ serpCompetitionMarkedAboveLabel() }}</dd>
            </div>
          </dl>
          <p class="url-serp-competition-hint muted" *ngIf="!markedCompetitorDomains.length">
            Mark competitors in settings to track marked rivals on this SERP.
          </p>

          <ng-container *ngIf="serpCompetitionTopThreeRows().length">
            <p class="url-serp-competition-leaders-intro muted">
              Top 3 on this SERP (excluding your URL):
            </p>
            <div class="url-serp-competition-leaders">
              <div *ngFor="let row of serpCompetitionTopThreeRows()" class="url-serp-competition-leader">
                <div class="url-serp-competition-leader-head">
                  <span class="url-serp-competition-pos">#{{ row.position }}</span>
                  <a
                    class="url-serp-competition-domain"
                    [href]="row.url"
                    target="_blank"
                    rel="noopener noreferrer"
                  >{{ row.domain }}</a>
                  <span class="url-serp-competition-date muted" *ngIf="row.publishedLabel">{{ row.publishedLabel }}</span>
                  <span [ngClass]="pageIntentFamilyChipClassFor(pageSerpRowIntentFamily(row))">{{ pageSerpRowIntentLabel(row) }}</span>
                  <span class="badge badge--warning" *ngIf="isTopSerpRival(row.url)">Top SERP rival</span>
                  <span class="badge badge--muted" *ngIf="row.isSocial">Social</span>
                  <span class="badge badge--muted" *ngIf="!row.isSocial && row.weakCompareBadge">{{
                    row.weakCompareBadge
                  }}</span>
                </div>
                <p class="url-serp-competition-title">{{ row.title }}</p>
                <p class="url-serp-competition-meta muted" *ngIf="row.descriptionPreview">{{ row.descriptionPreview }}</p>
                <button
                  type="button"
                  class="btn btn--sm url-serp-competition-compare-btn"
                  *ngIf="serpCompetitionShowQuickCompare(row)"
                  (click)="openQuickCompareWithUrl(row.url)"
                >
                  Quick Compare
                </button>
                <p class="url-serp-competition-social-note muted" *ngIf="row.isSocial">
                  Social result — listed for context; compare not available.
                </p>
                <p class="url-serp-competition-social-note muted" *ngIf="!row.isSocial && row.isWeakCompareTarget">
                  {{ row.weakCompareBadge }} result — not a useful SEO compare target; try the next editorial rival.
                </p>
                <button
                  type="button"
                  class="btn btn--sm btn--warning url-serp-rival-gap-btn"
                  *ngIf="!row.isSocial && !row.isWeakCompareTarget && serpRivalMissingSectionsLabel(row.url) as gapLabel"
                  (click)="openSerpRivalSectionCompare(row.url)"
                >
                  {{ gapLabel }} — see what they cover
                </button>
                <button
                  type="button"
                  class="btn btn--sm btn--warning url-serp-rival-gap-btn"
                  *ngIf="!row.isSocial && !row.isWeakCompareTarget && serpRivalRankDrivenGapsLabel(row.url) as rdLabel"
                  (click)="toggleTopSerpRivalRankDrivenPanel()"
                >
                  {{ rdLabel }} — why they rank
                </button>
              </div>
            </div>
            <div
              class="card card--pad card--edge card--warning url-serp-rank-driven-card"
              *ngIf="topSerpRivalRankDrivenOpen && topSerpRivalRankDrivenGaps().length"
            >
              <p class="url-serp-rank-driven-lead">
                Keywords they rank for that you miss — with on-page passages
              </p>
              <ul class="url-serp-rank-driven-items">
                <li
                  *ngFor="let gap of topSerpRivalRankDrivenGaps()"
                  class="url-topic-ownership-rank-driven-item"
                >
                  <div class="url-topic-ownership-rank-driven-main">
                    <span class="badge badge--warning">rank-driven</span>
                    {{ gap.evidenceLine }}
                  </div>
                  <div class="url-rank-driven-passage" *ngIf="gap.rivalPassageHeading || gap.rivalPassage">
                    <span class="url-rank-driven-passage-label">Their page</span>
                    <div class="url-rank-driven-passage-heading-row" *ngIf="gap.rivalPassageHeading">
                      <span
                        class="url-rank-driven-heading-tag"
                        *ngIf="rankDrivenHeadingTag(gap.rivalPassageHeadingLevel, gap.rivalPassageKind)"
                      >
                        {{ rankDrivenHeadingTag(gap.rivalPassageHeadingLevel, gap.rivalPassageKind) }}
                      </span>
                      <p class="url-rank-driven-passage-heading">
                        <ng-container
                          *ngFor="
                            let seg of rankDrivenPassageSegments(gap.keyword, gap.rivalPassageHeading)
                          "
                        >
                          <mark *ngIf="seg.highlight">{{ seg.text }}</mark>
                          <ng-container *ngIf="!seg.highlight">{{ seg.text }}</ng-container>
                        </ng-container>
                      </p>
                    </div>
                    <p class="url-rank-driven-passage-text" *ngIf="gap.rivalPassage">
                      <ng-container
                        *ngFor="let seg of rankDrivenPassageSegments(gap.keyword, gap.rivalPassage)"
                      >
                        <mark *ngIf="seg.highlight">{{ seg.text }}</mark>
                        <ng-container *ngIf="!seg.highlight">{{ seg.text }}</ng-container>
                      </ng-container>
                    </p>
                  </div>
                  <div class="url-rank-driven-passage url-rank-driven-passage--yours">
                    <span class="url-rank-driven-passage-label">Your page</span>
                    <div class="url-rank-driven-passage-heading-row" *ngIf="gap.focalPassageHeading">
                      <span
                        class="url-rank-driven-heading-tag"
                        *ngIf="rankDrivenHeadingTag(gap.focalPassageHeadingLevel, gap.focalPassageKind)"
                      >
                        {{ rankDrivenHeadingTag(gap.focalPassageHeadingLevel, gap.focalPassageKind) }}
                      </span>
                      <p class="url-rank-driven-passage-heading">
                        <ng-container
                          *ngFor="
                            let seg of rankDrivenPassageSegments(gap.keyword, gap.focalPassageHeading)
                          "
                        >
                          <mark *ngIf="seg.highlight">{{ seg.text }}</mark>
                          <ng-container *ngIf="!seg.highlight">{{ seg.text }}</ng-container>
                        </ng-container>
                      </p>
                    </div>
                    <p class="url-rank-driven-passage-text" *ngIf="gap.focalPassage">
                      <ng-container
                        *ngFor="let seg of rankDrivenPassageSegments(gap.keyword, gap.focalPassage)"
                      >
                        <mark *ngIf="seg.highlight">{{ seg.text }}</mark>
                        <ng-container *ngIf="!seg.highlight">{{ seg.text }}</ng-container>
                      </ng-container>
                    </p>
                    <p
                      class="url-rank-driven-passage-missing muted"
                      *ngIf="!gap.focalPassage && !gap.focalPassageHeading"
                    >
                      No matching passage for this keyword.
                    </p>
                  </div>
                  <p
                    class="url-rank-driven-mismatch"
                    *ngIf="gap.queryFamilyMismatch && gap.mismatchNote"
                  >
                    {{ gap.mismatchNote }}
                  </p>
                </li>
              </ul>
            </div>
            <ul class="url-serp-competition-rivals" *ngIf="serpCompetitionMarkedRivalsExtra(competition).length">
              <li *ngFor="let rival of serpCompetitionMarkedRivalsExtra(competition)" class="url-serp-competition-rival">
                <div class="url-serp-competition-rival-head">
                  <span class="url-serp-competition-pos">#{{ rival.position }}</span>
                  <span class="url-serp-competition-domain">{{ rival.domain }}</span>
                  <span class="badge badge--warning" *ngIf="isTopSerpRival(rival.url)">Top SERP rival</span>
                </div>
                <p class="url-serp-competition-title">{{ rival.title }}</p>
                <button type="button" class="btn btn--sm url-serp-competition-compare-btn" (click)="openQuickCompareWithUrl(rival.url)">
                  Quick Compare
                </button>
                <button
                  type="button"
                  class="btn btn--sm btn--warning url-serp-rival-gap-btn"
                  *ngIf="serpRivalMissingSectionsLabel(rival.url) as gapLabel"
                  (click)="openSerpRivalSectionCompare(rival.url)"
                >
                  {{ gapLabel }} — see what they cover
                </button>
              </li>
            </ul>
            <p class="url-serp-competition-note muted" *ngIf="isDevView()">
              Quick Compare crawls editorial pages on demand (~$0.01-0.05) if never analyzed; cached afterward.
            </p>
            <p class="url-serp-competition-note muted" *ngIf="competition.markedAboveYou">
              Uses workspace crawl when available.
            </p>
          </ng-container>

          <p class="url-serp-competition-note muted" *ngIf="!serpCompetitionTopThreeRows().length">
            No other domains in the top 3 slots for this query.
          </p>
        </article>
        </ng-container>

        <article
          class="card card--pad url-focus-card url-serp-subcard url-serp-subcard-phrase"
          *ngIf="showEmbedSerpPhraseCard() && buildSerpPhraseIssuePayload() as serpPhrasePayload"
          [ngClass]="planV2CardExitClass(serpPhrasePayload.issueKey)"
        >
          <div class="url-focus-card-top">
            <!-- "& entities" is the vocabulary row, which the review version
                 states as a sentence. Inventory keeps the full name and the
                 full card. -->
            <h4 class="url-focus-card-title">
              {{ embedReviewSurface ? 'Answer structure' : 'Answer structure &amp; entities' }}
            </h4>
            <span
              class="badge badge--caps"
              *ngIf="showSerpPhraseMatchActionChip()"
              [ngClass]="diagnosisChipClass(serpPhraseMatchActionChipTone())"
            >{{ serpPhraseMatchActionChipLabel() }}</span>
          </div>

          <!-- ── Review surface: the decision, not the dossier ────────────────
               Instruction, the words this page never uses, and both openings
               side by side. Rules and reasoning in `reviewAnswerStructure()`. -->
          <ng-container *ngIf="reviewAnswerStructure() as answer">
            <p class="review-answer__brief" *ngIf="answer.instruction">{{ answer.instruction }}</p>

            <!-- Only the two groups that ask for something: "elsewhere" is a
                 placement call, "missing" is a gap. Terms already in the right
                 place are on url-detail, not here — see the note in
                 `reviewAnswerVocabGroups`. -->
            <p class="review-answer__vocab" *ngIf="answer.vocab as vocab">
              <span class="review-answer__vocab-group" *ngIf="vocab.onPage.length">
                <span class="review-answer__vocab-label">Elsewhere on the page</span>
                <span class="review-answer__term review-answer__term--on" *ngFor="let term of vocab.onPage">{{ term }}</span>
              </span>
              <span class="review-answer__vocab-group" *ngIf="vocab.missing.length">
                <span class="review-answer__vocab-label">Missing</span>
                <span class="review-answer__term review-answer__term--missing" *ngFor="let term of vocab.missing">{{ term }}</span>
              </span>
            </p>

            <div class="review-answer__panel" *ngIf="answer.yours as yours">
              <!-- "How", not "what": this card compares the SHAPE of the answer,
                   and the passage is not always the opening — it is whichever
                   section comes closest to the SERP's shape. -->
              <p class="review-answer__label">How you answer</p>
              <!-- Unmarked on purpose: the only marks available here are
                   vocabulary, and this card is about structure. -->
              <p class="review-answer__quote">“{{ yours }}”</p>
            </div>

            <div class="review-answer__panel" *ngIf="answer.theirs.length">
              <p class="review-answer__label">
                How they answer
              </p>
              <div class="review-answer__winner" *ngFor="let win of answer.theirs">
                <p class="review-answer__src">#{{ win.position }} · {{ win.domain }}</p>
                <!-- The mark is the finding: the span the analyser identified as
                     the winner's answer structure. -->
                <p class="review-answer__quote">“<ng-container *ngFor="let seg of win.segments"
                  ><mark class="url-serp-contract-term" *ngIf="seg.emphasized">{{ seg.text }}</mark
                  ><ng-container *ngIf="!seg.emphasized">{{ seg.text }}</ng-container></ng-container
                >”</p>
              </div>
            </div>
          </ng-container>

          <ng-container *ngIf="!embedReviewSurface">
          <div class="url-serp-intent-head" *ngIf="pageIntentDiagnosis?.answerStructureFamily">
            <div class="url-serp-intent-row">
              <span class="url-serp-intent-row-title muted">Answer structure</span>
              <span class="url-serp-intent-pair">
                <span class="url-serp-intent-pair-label muted">SERP</span>
                <span [ngClass]="answerStructureSerpChipClass()">{{ answerStructureFamilyLabel(pageIntentDiagnosis?.answerStructureFamily) }}</span>
              </span>
              <span class="url-serp-intent-pair">
                <span class="url-serp-intent-pair-label muted">Yours</span>
                <span [ngClass]="answerStructureYourChipClass()">{{ answerStructureYourChipLabel() }}</span>
              </span>
            </div>
          </div>
          <p class="url-serp-intent-brief muted" *ngFor="let line of answerStructureBriefLines()">{{ line }}</p>
          <p class="url-serp-compare-legend muted" *ngIf="hasSearchIntentStructureEvidence()">
            <span class="url-serp-legend-green">Green</span> = answer structure (how winners deliver) ·
            <span class="url-serp-legend-blue" *ngIf="hasSearchIntentQuestionEvidence()">blue</span><ng-container *ngIf="hasSearchIntentQuestionEvidence()"> = query question restated on-page</ng-container>
          </p>
          <p class="url-serp-compare-legend muted" *ngIf="!hasSearchIntentStructureEvidence() && hasSearchIntentQuestionEvidence()">
            <span class="url-serp-legend-blue">Blue</span> = query question restated on-page (strong optimize-this cue)
          </p>
          <p class="url-serp-compare-legend muted" *ngIf="hasLexicalVocabChips()">
            <span class="url-serp-legend-green">Green</span> = in chosen passage ·
            <span class="url-serp-legend-amber">yellow</span> = elsewhere on page ·
            <span class="url-serp-legend-red">red</span> = absent · hover chip → all matching snippets
          </p>
          <p class="url-serp-vocab-fragmented muted" *ngIf="showFragmentedSerpVocabularyNote()">
            No shared vocabulary across top snippets — compare structure and table highlights.
          </p>
          <p
            class="url-serp-vocab-chips"
            *ngIf="hasLexicalVocabChips()"
            (mouseleave)="clearVocabChipHover()"
          >
            <span
              class="badge url-serp-vocab-chip-interactive"
              *ngFor="let chip of serpVocabChipRows()"
              [ngClass]="serpVocabChipClass(chip.tone)"
              [class.url-serp-vocab-chip-active]="isVocabChipHovered(chip.term)"
              [title]="pageIntentVocabChipTitle(chip.term)"
              (mouseenter)="onVocabChipHover(chip.term)"
              (focus)="onVocabChipHover(chip.term)"
              (blur)="clearVocabChipHover()"
              tabindex="0"
            >{{ chip.term }}</span>
          </p>

          <div class="url-serp-compare" *ngIf="hasPageIntentCompare()">
            <div class="url-serp-compare-col url-serp-compare-yours" #serpCompareYours>
              <h6 class="url-serp-compare-heading url-serp-compare-heading-plain">our page</h6>

              <section
                class="url-serp-page-section"
                *ngFor="let structurePassage of searchIntentStructurePagePassages()"
                [class.url-serp-page-section--closest]="structurePassage.matchKind === 'closest'"
              >
                <h6 class="url-serp-page-section-label url-serp-page-section-label-row">
                  <span *ngIf="structurePassage.matchKind === 'match'">Matching answer structure</span>
                  <span
                    *ngIf="structurePassage.matchKind === 'closest'"
                    class="url-serp-structure-closest-label"
                  >Closest — not an exact match</span>
                  <span
                    class="url-serp-structure-match-pct"
                    *ngIf="structurePassage.fitPct != null"
                    [class.url-serp-structure-match-pct--closest]="structurePassage.matchKind === 'closest'"
                    [class.url-serp-structure-match-pct--strong]="
                      structurePassage.matchKind === 'match' && (structurePassage.fitPct || 0) >= 70
                    "
                    [class.url-serp-structure-match-pct--mid]="
                      structurePassage.matchKind === 'match' &&
                      (structurePassage.fitPct || 0) >= 40 &&
                      (structurePassage.fitPct || 0) < 70
                    "
                    [attr.title]="answerStructurePassageFitTitle(structurePassage)"
                  >{{ structurePassage.fitPct }}%</span>
                </h6>
                <p
                  class="url-serp-structure-closest-note"
                  *ngIf="structurePassage.matchKind === 'closest'"
                >
                  Exact
                  <strong>{{ answerStructureFamilyLabel(pageIntentDiagnosis?.answerStructureFamily) }}</strong>
                  not found on the page. Showing the nearest delivery pattern
                  (<strong>{{ answerStructureFamilyLabel(structurePassage.family || pageIntentDiagnosis?.answerStructurePageFamily) }}</strong>)
                  — rewrite the intro or primary heading to match winners.
                </p>
                <div class="url-serp-structure-heading" *ngIf="structurePassageHeading(structurePassage.label) as heading">
                  <span class="url-serp-heading-tag" *ngIf="heading.tag">{{ heading.tag }}</span>
                  <span
                    class="url-serp-heading-title"
                    *ngIf="heading.title"
                    [class.url-serp-question-term]="structurePassage.headingIsQuestion"
                    [attr.title]="
                      structurePassage.headingIsQuestion
                        ? 'This heading restates the target query — strong passage to optimize'
                        : null
                    "
                  >{{ heading.title }}</span>
                </div>
                <p class="url-serp-compare-text url-serp-structure-passage">
                  <ng-container
                    *ngFor="let seg of searchIntentStructurePassageSegments(
                      structurePassage.text,
                      structurePassage.evidenceSpans,
                      structurePassage.questionSpans,
                      structurePassage.matchKind
                    )"
                  >
                    <mark
                      *ngIf="seg.emphasized"
                      [ngClass]="structurePassageSegmentClass(seg, structurePassage)"
                    >{{ seg.text }}</mark>
                    <ng-container *ngIf="!seg.emphasized">{{ seg.text }}</ng-container>
                  </ng-container>
                </p>
              </section>

              <ng-container *ngIf="!searchIntentStructurePagePassages().length">
                <section
                  class="url-serp-page-section"
                  *ngIf="pageIntentDiagnosis?.yourBestSection as best"
                >
                  <h6 class="url-serp-page-section-label">{{ best.label || 'Best section' }}</h6>
                  <p class="url-serp-compare-text">
                    <ng-container *ngFor="let seg of pageIntentYourSectionSegments(best.text)">
                      <mark class="url-serp-contract-term" *ngIf="seg.emphasized">{{ seg.text }}</mark>
                      <ng-container *ngIf="!seg.emphasized">{{ seg.text }}</ng-container>
                    </ng-container>
                  </p>
                </section>
                <p class="muted url-serp-compare-empty" *ngIf="!pageIntentDiagnosis?.yourBestSection?.text">
                  No crawled section text available for this URL.
                </p>

                <section
                  class="url-serp-page-section url-serp-page-section-secondary"
                  *ngIf="pageIntentDiagnosis?.yourIntroSection as intro"
                >
                  <h6 class="url-serp-page-section-label">{{ intro.label }}</h6>
                  <p class="url-serp-compare-text">
                    <ng-container *ngFor="let seg of pageIntentYourSectionSegments(intro.text)">
                      <mark class="url-serp-contract-term" *ngIf="seg.emphasized">{{ seg.text }}</mark>
                      <ng-container *ngIf="!seg.emphasized">{{ seg.text }}</ng-container>
                    </ng-container>
                  </p>
                </section>
              </ng-container>
            </div>

            <div
              class="url-serp-compare-col url-serp-compare-winners"
              [class.url-serp-compare-winners-fill]="winnerExcerptsExpanded"
              #serpCompareWinners
            >
              <h6 class="url-serp-compare-heading">{{ hoveredVocabChip ? 'Matching snippets' : 'Top winner excerpts' }}</h6>
              <ng-container *ngIf="hoveredVocabChip as chip; else winnerExcerptsDefault">
                <p class="url-serp-compare-chip-active muted">“{{ chip }}” in top 10 · move off chips to restore excerpts</p>
                <ng-container *ngIf="pageSerpCompareWinnerRows().length; else noWinnerChipMatches">
                  <div class="url-serp-compare-winners-list">
                    <div class="url-serp-compare-excerpt" *ngFor="let row of pageSerpCompareWinnerRows(); trackBy: trackPageSerpRow">
                      <span class="url-serp-compare-meta">#{{ row.position }} · {{ row.domain }}</span>
                      <p class="url-serp-compare-text url-serp-compare-text-chip-hover">
                        <ng-container *ngFor="let seg of pageSerpCompareWinnerRowSegments(row)">
                          <mark class="url-serp-contract-term url-serp-chip-pick" *ngIf="seg.emphasized">{{ seg.text }}</mark>
                          <ng-container *ngIf="!seg.emphasized">{{ seg.text }}</ng-container>
                        </ng-container>
                      </p>
                    </div>
                  </div>
                </ng-container>
                <ng-template #noWinnerChipMatches>
                  <p class="url-serp-compare-empty muted">No top-10 snippets contain “{{ hoveredVocabChip }}”.</p>
                </ng-template>
              </ng-container>
              <ng-template #winnerExcerptsDefault>
                <div class="url-serp-compare-winners-list">
                  <div class="url-serp-compare-excerpt" *ngFor="let excerpt of searchIntentWinnerExcerpts()">
                    <span class="url-serp-compare-meta">#{{ excerpt.position }} · {{ excerpt.domain }}</span>
                    <p class="url-serp-compare-text">
                      <ng-container *ngFor="let seg of searchIntentExcerptSegments(excerpt)">
                        <mark class="url-serp-contract-term" *ngIf="seg.emphasized">{{ seg.text }}</mark>
                        <ng-container *ngIf="!seg.emphasized">{{ seg.text }}</ng-container>
                      </ng-container>
                    </p>
                  </div>
                </div>
                <button
                  type="button"
                  class="url-serp-compare-show-more"
                  *ngIf="canExpandWinnerExcerpts()"
                  (click)="expandWinnerExcerpts()"
                >
                  Show more… ({{ searchIntentWinnerExcerptsHiddenCount() }} more)
                </button>
                <button
                  type="button"
                  class="url-serp-compare-show-more"
                  *ngIf="winnerExcerptsExpanded && searchIntentWinnerExcerptsHiddenCount() > 0"
                  (click)="collapseWinnerExcerpts()"
                >
                  Show less
                </button>
                <p class="url-serp-compare-idle muted" *ngIf="!searchIntentWinnerExcerpts().length">
                  No winner excerpts available for this SERP.
                </p>
              </ng-template>
            </div>
          </div>
          </ng-container>
          <app-plan-v2-issue-card-actions
            [reviewSelect]="embedReviewSurface"
            [reviewPending]="reviewSupportingPagesPending()"
            *ngIf="showIssueCardActions(serpPhrasePayload)"
            [payload]="serpPhrasePayload"
            [domain]="planV2IssueDomain()"
          />
        </article>

        <div
          class="url-serp-kd-intent-stack"
          *ngIf="showEmbedSerpDifficultyCard() || showEmbedSerpIntentCard()"
        >
        <article
          class="card card--pad url-focus-card url-serp-subcard url-serp-subcard-kd"
          *ngIf="showEmbedSerpDifficultyCard() && buildSerpDifficultyIssuePayload() as serpKdPayload"
          [ngClass]="planV2CardExitClass(serpKdPayload.issueKey)"
        >
          <div class="url-focus-card-top">
            <h4 class="url-focus-card-title">SERP difficulty</h4>
            <span class="badge badge--caps" [ngClass]="diagnosisChipClass(serpDifficultyActionChipTone())">{{
              serpDifficultyActionChipLabel()
            }}</span>
          </div>
          <p class="url-serp-kd-value" [attr.title]="pageSerpKeywordDifficultyTitle()">{{ serpKeywordDifficultyShort() }}</p>
          <p class="url-serp-kd-sub muted" *ngIf="serpKeywordDifficultyHint() as hint">{{ hint }}</p>
          <p class="url-serp-kd-query muted">{{ pageSerpTargetQueryLabel() }}</p>
        </article>

        <article
          class="card card--pad url-focus-card url-serp-subcard url-serp-subcard-intent"
          *ngIf="showEmbedSerpIntentCard() && buildSerpIntentIssuePayload() as serpIntentPayload"
          [ngClass]="planV2CardExitClass(serpIntentPayload.issueKey)"
        >
          <div class="url-focus-card-top">
            <h4 class="url-focus-card-title">Page type</h4>
            <span class="badge badge--caps" [ngClass]="diagnosisChipClass(pageIntentMatchActionChipTone())">{{
              pageIntentMatchActionChipLabel()
            }}</span>
          </div>
          <div class="url-serp-intent-head" *ngIf="showPageIntentAlignmentRow()">
            <span class="url-serp-intent-pair">
              <span class="url-serp-intent-pair-label muted">SERP</span>
              <span [ngClass]="pageIntentSerpFamilyChipClass()">{{ pageIntentFamilyLabel(pageIntentDiagnosis?.serpFamily) }}</span>
            </span>
            <span class="url-serp-intent-pair">
              <span class="url-serp-intent-pair-label muted">Yours</span>
              <span [ngClass]="pageIntentYourFamilyChipClass()">{{ pageIntentYourPageFamilyLabel() }}</span>
            </span>
          </div>
          <table
            class="table table--dense url-serp-mixed-table"
            *ngIf="pageIntentDiagnosis?.serpFamily === 'mixed' && pageIntentDiagnosis?.serpMixedIntentSamples?.length"
          >
            <thead>
              <tr>
                <th class="url-serp-mixed-col-pos">#</th>
                <th class="url-serp-mixed-col-domain">Domain</th>
                <th class="url-serp-mixed-col-intent">Type</th>
              </tr>
            </thead>
            <tbody>
              <tr *ngFor="let sample of pageIntentDiagnosis?.serpMixedIntentSamples">
                <td class="url-serp-mixed-col-pos">{{ sample.position }}</td>
                <td class="url-serp-mixed-col-domain" [title]="sample.domain">{{ sample.domain }}</td>
                <td class="url-serp-mixed-col-intent">
                  <span [ngClass]="pageIntentFamilyChipClassFor(sample.family)">{{ pageIntentFamilyLabel(sample.family) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
          <p class="url-serp-intent-brief muted" *ngFor="let line of pageTypeBriefLines()">{{ line }}</p>
          <!-- What the verdict above looks like, so it can be checked without leaving the belt. -->
          <div class="url-page-type-examples" *ngIf="pageTypeExamples().length">
            <p class="url-page-type-examples-label muted">{{ pageTypeExamplesLabel() }}</p>
            <p class="url-page-type-example" *ngFor="let example of pageTypeExamples()">
              <span class="url-page-type-example-pos">#{{ example.position }}</span>
              <span class="url-page-type-example-domain">{{ example.domain }}</span>
              <span class="url-page-type-example-title" [title]="example.title">{{ example.title }}</span>
            </p>
          </div>
          <app-plan-v2-issue-card-actions
            [reviewSelect]="embedReviewSurface"
            [reviewPending]="reviewSupportingPagesPending()"
            *ngIf="showIssueCardActions(serpIntentPayload)"
            [payload]="serpIntentPayload"
            [domain]="planV2IssueDomain()"
          />
        </article>
        </div>
      </div>

      <div class="url-serp-expanded" *ngIf="showEmbedSerpResultsTable()">
        <div class="url-serp-table-wrap">
          <table class="table table--dense url-serp-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Domain</th>
                <th>Title</th>
                <th>Intent</th>
                <th>Type</th>
              </tr>
            </thead>
            <tbody>
              <tr
                *ngFor="let row of pageSerp?.results; trackBy: trackPageSerpRow"
                [class.url-serp-row-you]="pageSerpResultCompetitorKind(row) === 'you'"
                [class.url-serp-row-marked]="pageSerpResultCompetitorKind(row) === 'marked'"
              >
                <td>{{ row.position }}</td>
                <td>
                  <a [href]="'https://' + row.domain" target="_blank" rel="noopener noreferrer">{{ row.domain }}</a>
                </td>
                <td class="url-serp-title-cell">
                  <a [href]="row.url" target="_blank" rel="noopener noreferrer">{{ row.title }}</a>
                  <p
                    class="url-serp-published"
                    *ngIf="pageSerpPublishedDate(row) as pubDate"
                    [ngClass]="pageSerpPublishedAgeClass(row)"
                  >{{ pubDate | date:'MMM d, yyyy' }}</p>
                  <p class="url-serp-snippet" *ngIf="row.description">
                    <ng-container *ngFor="let seg of pageSerpSnippetSegments(row)">
                      <strong *ngIf="seg.emphasized; else serpSnippetPlain">{{ seg.text }}</strong>
                      <ng-template #serpSnippetPlain>{{ seg.text }}</ng-template>
                    </ng-container>
                  </p>
                </td>
                <td class="url-serp-intent-col">
                  <span [ngClass]="pageIntentFamilyChipClassFor(pageSerpRowIntentFamily(row))">{{
                    pageSerpRowIntentLabel(row)
                  }}</span>
                </td>
                <td>
                  <span
                    class="badge badge--pill"
                    [class.badge--success]="pageSerpResultCompetitorKind(row) === 'you'"
                    [class.badge--warning]="pageSerpResultCompetitorKind(row) === 'marked'"
                  >{{ pageSerpResultCompetitorLabel(row) }}</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </section>

    <section class="url-focus-row url-focus-row--technical" *ngIf="showEmbedTechnicalSection()" aria-labelledby="url-technical-heading">
      <h3 id="url-technical-heading" class="url-focus-row-heading">Technical</h3>
      <div class="url-focus-row-cards url-focus-row-cards--technical">
        <article
          class="card card--pad url-focus-card url-focus-card-technical-critical"
          *ngIf="showEmbedTechnicalIssuesCard() && buildTechnicalCriticalIssuePayload() as techCritPayload"
          [class.url-focus-card--composing]="technicalCriticalComposing"
          [ngClass]="planV2CardExitClass(techCritPayload.issueKey)"
        >
          <div class="url-focus-card-top">
            <h4 class="url-focus-card-title">Issues</h4>
            <span class="badge badge--caps" [ngClass]="diagnosisChipClass(technicalIssuesActionChipTone())">{{
              technicalIssuesActionChipLabel()
            }}</span>
          </div>
          <ng-container *ngIf="!technicalCriticalComposing">
            <p class="url-technical-none muted" *ngIf="!hasTechnicalCriticalIssues()">None</p>
            <p class="url-technical-settled muted" *ngIf="reviewSettledSiteWideLabel(technicalCriticalIssueViews()) as settled">
              {{ settled }}
            </p>
            <ul *ngIf="hasTechnicalCriticalIssues()" class="url-focus-list url-technical-issue-list">
              <ng-container
                *ngFor="let issue of reviewTechnicalIssues(technicalCriticalIssueViews()); let i = index"
                [ngTemplateOutlet]="onpageIssueItem"
                [ngTemplateOutletContext]="{
                  issue: issue,
                  kind: 'critical',
                  index: i,
                  sending: reviewTechnicalCardSending(techCritPayload),
                  cardKey: techCritPayload.issueKey
                }"
              ></ng-container>
            </ul>
          </ng-container>
          <app-plan-v2-issue-card-actions
            [reviewSelect]="embedReviewSurface"
            [reviewPending]="reviewSupportingPagesPending()"
            *ngIf="showIssueCardActions(techCritPayload)"
            [payload]="techCritPayload"
            [domain]="planV2IssueDomain()"
            (composingChange)="technicalCriticalComposing = $event"
          />
        </article>

        <article
          class="card card--pad url-focus-card url-focus-card-technical-warnings"
          *ngIf="showEmbedTechnicalWarningsCard() && buildTechnicalWarningsIssuePayload() as techWarnPayload"
          [class.url-focus-card--composing]="technicalWarningsComposing"
          [ngClass]="planV2CardExitClass(techWarnPayload.issueKey)"
        >
          <div class="url-focus-card-top">
            <h4 class="url-focus-card-title">Warnings</h4>
            <span class="badge badge--caps" [ngClass]="diagnosisChipClass(technicalWarningsActionChipTone())">{{
              technicalWarningsActionChipLabel()
            }}</span>
          </div>
          <ng-container *ngIf="!technicalWarningsComposing">
            <p class="url-technical-none muted" *ngIf="!hasTechnicalWarnings()">None</p>
            <p class="url-technical-settled muted" *ngIf="reviewSettledSiteWideLabel(technicalWarningIssueViews()) as settled">
              {{ settled }}
            </p>
            <ul *ngIf="hasTechnicalWarnings()" class="url-focus-list url-technical-issue-list">
              <ng-container
                *ngFor="let issue of reviewTechnicalIssues(technicalWarningIssueViews()); let i = index"
                [ngTemplateOutlet]="onpageIssueItem"
                [ngTemplateOutletContext]="{
                  issue: issue,
                  kind: 'warning',
                  index: i,
                  sending: reviewTechnicalCardSending(techWarnPayload),
                  cardKey: techWarnPayload.issueKey
                }"
              ></ng-container>
            </ul>
          </ng-container>
          <app-plan-v2-issue-card-actions
            [reviewSelect]="embedReviewSurface"
            [reviewPending]="reviewSupportingPagesPending()"
            *ngIf="showIssueCardActions(techWarnPayload)"
            [payload]="techWarnPayload"
            [domain]="planV2IssueDomain()"
            (composingChange)="technicalWarningsComposing = $event"
          />
        </article>

        <ng-template
          #onpageIssueItem
          let-issue="issue"
          let-kind="kind"
          let-index="index"
          let-sending="sending"
          let-cardKey="cardKey"
        >
          <li
            class="url-technical-issue-item"
            [class.url-technical-issue-item--tickable]="embedReviewSurface"
            [class.url-technical-issue-item--unticked]="
              embedReviewSurface && (sending === false || !isTechnicalCheckTicked(issue, cardKey))
            "
          >
            <!-- Review surface only: which of this card's findings the send
                 covers. The card tick is still the card's decision; this is the
                 line inside it. Unticking every one of them unticks the card,
                 because a work item that names no work is not a work item. -->
            <input
              *ngIf="embedReviewSurface"
              class="url-technical-issue-tick"
              type="checkbox"
              [checked]="sending !== false && isTechnicalCheckTicked(issue, cardKey)"
              [attr.aria-label]="'Fix ' + issue.title"
              (change)="toggleTechnicalCheck(issue, cardKey, $event)"
            />
            <button
              *ngIf="issue.details.length"
              type="button"
              class="url-technical-issue-title url-technical-issue-title--toggle"
              [attr.aria-expanded]="isTechnicalIssueDetailsExpanded(kind, index)"
              (click)="toggleTechnicalIssueDetails(kind, index)"
            >
              <span class="url-technical-issue-chevron" [class.rotated]="isTechnicalIssueDetailsExpanded(kind, index)"
                >▼</span
              >
              <span class="url-technical-issue-title-text">{{ issue.title }}</span>
            </button>
            <span *ngIf="!issue.details.length" class="url-technical-issue-title">{{ issue.title }}</span>
            <!-- Review surface only: which of the two jobs this finding is. -->
            <span
              class="badge badge--pill url-technical-issue-scope"
              *ngIf="technicalIssueScopeLabel(issue) as scope"
              [class.url-technical-issue-scope--site]="scope === 'Site-wide'"
              >{{ scope }}</span
            >
            <span
              class="url-technical-issue-muted muted"
              *ngIf="technicalCheckMutedNote(issue, cardKey) as note"
              >{{ note }}</span
            >
            <ul
              *ngIf="issue.details.length && isTechnicalIssueDetailsExpanded(kind, index)"
              class="url-technical-issue-details"
            >
              <li *ngFor="let detail of issue.details">{{ detail }}</li>
            </ul>
          </li>
        </ng-template>

        <article class="card card--pad url-focus-card url-focus-card-technical-perf" *ngIf="!isPlanV2Embed()">
          <h4 class="url-focus-card-title">Technical performance</h4>
          <dl class="url-focus-dl url-technical-perf-dl">
            <div class="url-focus-dl-row">
              <dt>TTI</dt>
              <dd>{{ formatTechnicalPerfMs(analysisData.timeToInteractive) }}</dd>
            </div>
            <div class="url-focus-dl-row">
              <dt>LCP</dt>
              <dd>{{ formatTechnicalPerfMs(analysisData.largestContentfulPaint) }}</dd>
            </div>
            <div class="url-focus-dl-row">
              <dt>FID</dt>
              <dd>{{ formatTechnicalPerfMs(analysisData.firstInputDelay) }}</dd>
            </div>
          </dl>
        </article>

        <article
          *ngIf="!isPlanV2Embed()"
          class="card card--pad url-focus-card url-focus-card-technical-links"
          [class.url-focus-card-technical-links--dev]="isDevView()"
        >
          <h4 class="url-focus-card-title">Internal links</h4>
          <p class="url-technical-none muted" *ngIf="!hasLinks()">None</p>
          <ng-container *ngIf="hasLinks()">
            <ng-container *ngIf="isDevView(); else technicalLinksUser">
              <div class="url-technical-link-stats">
                <div class="url-technical-link-stat">
                  <span class="url-technical-link-stat-value">{{ getLinkStats().structural }}</span>
                  <span class="url-technical-link-stat-label">Structural</span>
                </div>
                <div class="url-technical-link-stat">
                  <span class="url-technical-link-stat-value">{{ getLinkStats().contextual }}</span>
                  <span class="url-technical-link-stat-label">Contextual</span>
                </div>
                <div class="url-technical-link-stat">
                  <span class="url-technical-link-stat-value">{{ getLinkStats().samePage }}</span>
                  <span class="url-technical-link-stat-label">Same page</span>
                </div>
                <div class="url-technical-link-stat" *ngIf="getLinkStats().unknown > 0">
                  <span class="url-technical-link-stat-value">{{ getLinkStats().unknown }}</span>
                  <span class="url-technical-link-stat-label">Unknown</span>
                </div>
              </div>
              <div class="links-controls url-technical-links-controls">
                <div class="filter-group">
                  <label>Filter:</label>
                  <select [(ngModel)]="linkFilter" class="input">
                    <option value="all">All Links</option>
                    <option value="structural">Structural</option>
                    <option value="contextual">Contextual</option>
                    <option value="same_page">Same Page</option>
                  </select>
                </div>
                <div class="filter-group">
                  <label>Sort:</label>
                  <select [(ngModel)]="linkSort" class="input">
                    <option value="type">By Type</option>
                    <option value="confidence">By Confidence</option>
                    <option value="anchor">By Anchor Text</option>
                    <option value="url">By URL</option>
                  </select>
                </div>
              </div>
              <div class="links-list url-technical-links-list">
                <div
                  *ngFor="let link of getFilteredLinks(); trackBy: trackByLink"
                  class="link-item"
                  [class.expanded]="link._expanded"
                >
                  <div class="link-header" (click)="toggleLinkDetails(link)">
                    <div class="link-main">
                      <span class="badge badge--solid link-badge" [class]="getLinkTypeClass(link.linkType)">
                        {{ getLinkTypeLabel(link.linkType) }}
                      </span>
                      <span class="link-anchor-text">{{ link.anchorText || '(no anchor text)' }}</span>
                    </div>
                    <div class="link-meta">
                      <span class="link-confidence" [class]="getConfidenceClass(link.confidence)">
                        {{ (link.confidence * 100).toFixed(0) }}%
                      </span>
                      <span class="link-chevron" [class.rotated]="link._expanded">▼</span>
                    </div>
                  </div>
                  <div class="link-url">
                    <a [href]="link.targetUrl" target="_blank" rel="noopener noreferrer" (click)="$event.stopPropagation()">
                      {{ link.targetRelativeUrl }}
                    </a>
                    <span class="link-flags">
                      <span *ngIf="!link.dofollow" class="flag flag-nofollow">nofollow</span>
                      <span *ngIf="link.isBroken" class="flag flag-broken">broken</span>
                    </span>
                  </div>
                  <div class="link-details" *ngIf="link._expanded">
                    <div class="detail-grid">
                      <div class="detail-item" *ngIf="link.classificationSignals?.linkFrequency !== undefined">
                        <span class="detail-label">Frequency:</span>
                        <span class="detail-value">
                          {{ link.classificationSignals.linkFrequency }} pages
                          <span *ngIf="link.classificationSignals.linkFrequencyRatio !== undefined">
                            ({{ (link.classificationSignals.linkFrequencyRatio * 100).toFixed(1) }}%)
                          </span>
                        </span>
                      </div>
                      <div class="detail-item" *ngIf="link.classificationSignals?.anchorTextLength !== undefined">
                        <span class="detail-label">Anchor Length:</span>
                        <span class="detail-value">{{ link.classificationSignals.anchorTextLength }} chars</span>
                      </div>
                      <div class="detail-item" *ngIf="link.classificationSignals?.urlPattern">
                        <span class="detail-label">URL Pattern:</span>
                        <span class="detail-value">{{ link.classificationSignals.urlPattern }}</span>
                      </div>
                      <div class="detail-item" *ngIf="link.classificationSignals?.isGenericAnchor !== undefined">
                        <span class="detail-label">Generic Anchor:</span>
                        <span class="detail-value">{{ link.classificationSignals.isGenericAnchor ? 'Yes' : 'No' }}</span>
                      </div>
                      <div class="detail-item" *ngIf="link.classificationSignals?.isSamePage !== undefined">
                        <span class="detail-label">Same Page:</span>
                        <span class="detail-value">{{ link.classificationSignals.isSamePage ? 'Yes' : 'No' }}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <p class="url-technical-none muted" *ngIf="getFilteredLinks().length === 0">No links match the current filter.</p>
            </ng-container>
            <ng-template #technicalLinksUser>
              <dl class="url-technical-link-counts">
                <div class="url-focus-dl-row">
                  <dt>Structural</dt>
                  <dd>{{ getLinkStats().structural }}</dd>
                </div>
                <div class="url-focus-dl-row">
                  <dt>Contextual</dt>
                  <dd>{{ getLinkStats().contextual }}</dd>
                </div>
                <div class="url-focus-dl-row">
                  <dt>Same page</dt>
                  <dd>{{ getLinkStats().samePage }}</dd>
                </div>
              </dl>
            </ng-template>
          </ng-container>
        </article>
      </div>
    </section>

    <ng-template #internalLinkSuggestionRows>
      <ul class="url-signals-detail-list url-signals-detail-list--links">
        <li
          *ngFor="let row of pageSignalInternalLinkDetailsForDisplay()"
          class="url-signals-link-row"
          [class.url-signals-link-row--tickable]="embedReviewSurface"
          [class.url-signals-link-row--unticked]="embedReviewSurface && !isInternalLinkTicked(row)"
        >
          <input
            *ngIf="embedReviewSurface"
            class="url-signals-link-tick"
            type="checkbox"
            [checked]="isInternalLinkTicked(row)"
            [attr.aria-label]="'Add link from ' + internalLinkRowDisplay(row).pagePath"
            (change)="toggleInternalLink(row, $event)"
          />
          <div class="url-signals-link-row-main" *ngIf="internalLinkRowDisplay(row) as display">
            <div class="internal-link-row-chips">
              <span class="badge badge--pill" [ngClass]="internalLinkDirectionChipClasses(row)">{{
                internalLinkDirectionChip(row)
              }}</span>
              <span class="badge badge--pill"
                >Relevance {{ formatInternalLinkScore(row.score) }}</span
              >
            </div>
            <!-- Review: the LIVE page, in a new tab.
                 The deep dive sends this into Inventory in the same tab, which
                 on a belt means losing your place in it to look at a page you
                 were about to decide about. What an operator wants here is the
                 page itself — the same promise the belt's own header URL makes
                 two rows up. Inventory keeps its in-app link; that surface is
                 where you go to dig. -->
            <a
              class="url-signals-detail-link url-signals-detail-link--path"
              *ngIf="embedReviewSurface; else inAppPathLink"
              [href]="display.pageUrl"
              target="_blank"
              rel="noopener noreferrer"
              [title]="display.pageUrl"
              >{{ display.pagePath }}</a
            >
            <ng-template #inAppPathLink>
              <a
                class="url-signals-detail-link url-signals-detail-link--path"
                [routerLink]="inventoryDetailLink(display.pageUrl)"
                [title]="display.pageUrl"
                >{{ display.pagePath }}</a
              >
            </ng-template>
            <span class="url-signals-detail-meta muted" *ngIf="row.matchedTopic">
              topic {{ row.matchedTopic }}</span
            >
            <p class="url-signals-placement muted" *ngIf="row.where?.sentence as sentence">
              <ng-container *ngIf="internalLinkPlacementParts(row) as parts; else signalPlacementPlain">
                “{{ parts.before }}<a
                  class="url-signals-placement-link"
                  [href]="display.linkHref"
                  target="_blank"
                  rel="noopener noreferrer"
                  >{{ parts.anchor }}</a
                >{{ parts.after }}”
              </ng-container>
              <ng-template #signalPlacementPlain>
                “{{ sentence }}”
              </ng-template>
            </p>
          </div>
        </li>
      </ul>
    </ng-template>

    <section
      class="url-focus-row url-focus-row--internal-links"
      *ngIf="showEmbedInternalLinksSection()"
      aria-labelledby="url-internal-links-row-heading"
    >
      <h3 id="url-internal-links-row-heading" class="url-focus-row-heading">Internal links</h3>
      <article
        #internalLinksSection
        class="card card--pad url-focus-card url-focus-card--internal-links"
        *ngIf="internalLinksIssuesPayload() as linksPayload"
        [class.url-focus-card--composing]="internalLinksComposing"
        [ngClass]="planV2CardExitClass(linksPayload.issueKey)"
      >
        <div class="url-focus-card-top">
          <h4 class="url-focus-card-title" id="url-internal-links-heading">Suggestions</h4>
          <span
            class="badge badge--caps"
            *ngIf="reviewInternalLinksCountLabel() as linkCount"
            [ngClass]="diagnosisChipClass('warn')"
            >{{ linkCount }}</span
          >
        </div>
        <ng-container *ngIf="!internalLinksComposing">
          <ng-container *ngTemplateOutlet="internalLinkSuggestionRows"></ng-container>
        </ng-container>
        <!-- Review: each link is its own decision filed on its own source page,
             so there is no card-level record for the shared actions component
             to write. The tick below is a master over those rows. -->
        <div class="review-links-actions" *ngIf="embedReviewSurface && reviewInternalLinkTickable()">
          <label class="plan-v2-review-tick">
            <input
              type="checkbox"
              [checked]="reviewInternalLinksSending()"
              (change)="toggleReviewInternalLinksCard($event)"
            />
            <span>{{ reviewInternalLinksSending() ? 'Sending' : 'Not this one' }}</span>
          </label>
        </div>
        <app-plan-v2-issue-card-actions
            [reviewSelect]="embedReviewSurface"
            [reviewPending]="reviewSupportingPagesPending()"
          *ngIf="!embedReviewSurface && showIssueCardActions(linksPayload)"
          [payload]="linksPayload"
          [domain]="planV2IssueDomain()"
          (composingChange)="onInternalLinksComposingChange($event)"
        />
      </article>
    </section>

    <section class="url-focus-row url-focus-row--signals" *ngIf="showEmbedSignalsSection()" aria-labelledby="url-signals-heading">
      <h3 id="url-signals-heading" class="url-focus-row-heading">Signals</h3>
      <article
        class="card card--pad url-focus-card url-signals-card"
        [ngClass]="isPlanV2Embed() ? planV2CardExitClass(planV2IssueKey('signals')) : {}"
      >
        <p class="url-signals-loading muted" *ngIf="pageSignalsLoading">Loading workspace signals…</p>

        <ng-container *ngIf="!pageSignalsLoading">
          <div class="page-signals" *ngIf="pageSignals.length > 0">
            <span
              class="badge badge--pill"
              *ngFor="let signal of pageSignals"
              [ngClass]="pageSignalChipClass(signal.kind)"
              [title]="signal.title"
            >{{ signal.label }}</span>
          </div>
          <p class="url-signals-empty muted" *ngIf="pageSignals.length === 0">
            No open action-plan or cluster signals for this URL (same rules as Inventory → Pages → Signals).
          </p>

          <div class="url-signals-plan-clusters" *ngIf="planClusters.length > 0">
            <h4 class="url-signals-detail-title">Plan clusters</h4>
            <p class="url-signals-cluster-tags">
              <span class="badge" *ngFor="let label of planClusters">{{ label }}</span>
            </p>
          </div>

          <div class="url-signals-detail" *ngIf="pageSignalCannibalDetails().length > 0">
            <h4 class="url-signals-detail-title">Cannibalization</h4>
            <ul class="url-signals-detail-list">
              <li *ngFor="let row of pageSignalCannibalDetails()">
                <span class="url-signals-detail-main">Open pair with</span>
                <a
                  class="url-signals-detail-link"
                  [routerLink]="inventoryDetailLink(pageSignalCannibalCounterpart(row))"
                  [title]="pageSignalCannibalCounterpart(row)"
                >{{ pageSignalCannibalCounterpart(row) }}</a>
              </li>
            </ul>
          </div>

          <div
            class="url-signals-detail url-signals-detail--internal-links"
            *ngIf="!isPlanV2Embed() && pageSignalInternalLinkDetails().length > 0"
          >
            <h4 class="url-signals-detail-title">Internal link suggestions</h4>
            <ng-container *ngTemplateOutlet="internalLinkSuggestionRows"></ng-container>
          </div>

          <div class="url-signals-detail" *ngIf="pageSignalUnclusteredDetail() as unclustered">
            <h4 class="url-signals-detail-title">Unclustered page</h4>
            <p class="url-signals-detail-line muted">
              Recommendation: <strong>{{ unclusteredRecommendationLabel(unclustered.recommendation) }}</strong>
            </p>
            <p class="url-signals-detail-line muted" *ngIf="unclustered.topTopics?.length">
              Top topics: {{ unclustered.topTopics.join(', ') }}
            </p>
          </div>

          <div class="url-signals-detail" *ngIf="hasPageSignalKind('no_cluster')">
            <h4 class="url-signals-detail-title">No plan cluster</h4>
            <p class="url-signals-detail-line muted">
              This URL is not in any workspace plan cluster for this domain. Assign clusters in Content Inventory → Plan.
            </p>
          </div>

          <p class="url-signals-footer" *ngIf="!isPlanV2Embed()">
            <a class="url-signals-inventory-link" routerLink="/inventory" (click)="openInventoryForSignals()">
              Open Content Inventory
            </a>
            <span class="muted"> to resolve signals on the Plan or Cannibalization tabs.</span>
          </p>
          <ng-container *ngIf="isPlanV2Embed() && buildSignalsIssuePayload() as signalsPayload">
            <app-plan-v2-issue-card-actions
            [reviewSelect]="embedReviewSurface"
            [reviewPending]="reviewSupportingPagesPending()"
              *ngIf="showIssueCardActions(signalsPayload)"
              [payload]="signalsPayload"
              [domain]="planV2IssueDomain()"
            />
          </ng-container>
        </ng-container>
      </article>
    </section>

    <section
      class="url-focus-row url-focus-row--dev-content"
      aria-labelledby="url-dev-content-heading"
      *ngIf="!isPlanV2Embed() && isDevView() && analysisData && hasContent()"
    >
      <h3 id="url-dev-content-heading" class="url-focus-row-heading">Page Content</h3>
      <div class="content-section url-focus-dev-page-content">
      <div class="content-header" (click)="toggleContentSection()">
        <h4 class="url-focus-card-title">{{ contentElements.length }} elements</h4>
        <span class="chevron" [class.collapsed]="contentSectionCollapsed">▼</span>
      </div>
      
      <div class="content-body" [class.collapsed]="contentSectionCollapsed">
        <div class="content-info">
          <span>Key content elements extracted from the page</span>
        </div>
        
        <div class="content-elements">
          <div 
            *ngFor="let element of contentElements; trackBy: trackByElement" 
            class="content-element"
            [class]="element.type">
            
            <!-- Headings -->
            <div *ngIf="element.type === 'heading'" class="heading-element">
              <span class="heading-tag">H{{ element.level }}</span>
              <span class="heading-text" [class]="getHeadingClass(element.level!)">
                {{ element.content }}
              </span>
            </div>
            
            <!-- Text Content -->
            <div *ngIf="element.type === 'text'" class="text-element">
              <span class="text-content">{{ element.content }}</span>
            </div>
            
            <!-- Links -->
            <div *ngIf="element.type === 'link'" class="link-element">
              <span class="link-text">{{ element.content }}</span>
              <span class="link-url">{{ element.href }}</span>
            </div>
            
            <!-- Tables -->
            <!--
              A table extracted from the crawled page. `tableData` is a flat
              cell list with an isHeader flag and no row breaks, so this is one
              row by construction -- it was a flex stack pretending to be a
              table before, and it is a real one now.
            -->
            <div *ngIf="element.type === 'table'" class="card table-element">
              <div class="card__header">{{ element.content }}</div>
              <table class="table table--dense">
                <tbody>
                  <tr class="table__row">
                    <ng-container *ngFor="let cell of element.tableData; trackBy: trackByElement">
                      <th *ngIf="cell.isHeader" scope="col">
                        <span class="cell-text">{{ cell.text }}</span>
                        <span *ngIf="cell.urls && cell.urls.length > 0" class="cell-links">
                          <a *ngFor="let url of cell.urls" [href]="url.url" target="_blank" class="cell-link">
                            {{ url.anchor_text || url.url }}
                          </a>
                        </span>
                      </th>
                      <td *ngIf="!cell.isHeader">
                        <span class="cell-text">{{ cell.text }}</span>
                        <span *ngIf="cell.urls && cell.urls.length > 0" class="cell-links">
                          <a *ngFor="let url of cell.urls" [href]="url.url" target="_blank" class="cell-link">
                            {{ url.anchor_text || url.url }}
                          </a>
                        </span>
                      </td>
                    </ng-container>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
    </section>

  </ng-container>
</section>

<div class="modal-backdrop" *ngIf="claimOverrideOpen" (click)="closeClaimOverrideModal()">
  <div class="card card--pad modal url-claim-override-modal" role="dialog" aria-labelledby="claim-override-title" (click)="$event.stopPropagation()">
    <h3 id="claim-override-title">Correct demand claim</h3>
    <p class="muted">Update what this URL is accountable for. Saves override and refreshes SERP for the new query.</p>
    <label class="field url-claim-override-field">
      <span class="field__label">Claim label</span>
      <input class="input" type="text" [(ngModel)]="claimOverrideLabel" [disabled]="claimOverrideSaving" />
    </label>
    <div class="url-claim-override-suggestions" *ngIf="claimOverrideSuggestions().length">
      <span class="muted">Suggestions:</span>
      <button
        type="button"
        class="btn-link-inline"
        *ngFor="let suggestion of claimOverrideSuggestions()"
        (click)="claimOverrideLabel = suggestion"
      >{{ suggestion }}</button>
    </div>
    <p class="url-claim-override-error" *ngIf="claimOverrideError">{{ claimOverrideError }}</p>
    <div class="modal-actions">
      <button type="button" class="btn-secondary" (click)="closeClaimOverrideModal()" [disabled]="claimOverrideSaving">Cancel</button>
      <button type="button" class="btn btn--accent" (click)="saveClaimOverride()" [disabled]="claimOverrideSaving">
        {{ claimOverrideSaving ? 'Saving & refreshing…' : 'Save & refresh' }}
      </button>
    </div>
  </div>
</div>

<app-inventory-quick-compare-drawer
  [open]="quickCompareOpen"
  [primaryUrl]="quickComparePrimaryUrl"
  [compareUrl]="quickCompareCompareUrl"
  [primaryDomainLabel]="extractDomainFromUrl(url || '')"
  [compareDomainLabel]="extractDomainFromUrl(quickCompareCompareUrl || '')"
  (closed)="onQuickCompareDrawerClose()"
  (openFullCompare)="onOpenFullCompareFromDrawer()"
/> 